import {
  Component,
  Input,
  SimpleChanges,
  Output,
  EventEmitter,
  OnChanges,
  OnDestroy,
  ChangeDetectionStrategy,
} from '@angular/core';
import { StorageService } from '../../../services/storage/storage.service';
import { PredefinedTemplatesService } from '../../../services/edit/predefined-templates.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SharedService } from '../../../services/shared/shared.service';
import { ColorsService } from './../../../services/colors/colors.service';
import { Subscription } from 'rxjs';
import * as $ from 'jquery';

export interface ColorInterface {
  _id: string;
  themeName: string;
  typeOf: string;
  shadeBase: string;
  shadeOne: string;
  shadeTwo: string;
  shadeThree: string;
  shadeFour: string;
  neutral: string;
  offsetOne: string;
  offsetTwo: string;
  offsetThree: string;
  offsetFour: string;
  accentOne: string;
  accentTwo: string;
  accentThree: string;
  accentFour: string;
  headerBackground: string;
  headerColor: string;
  footerBackground: string;
  footerColor: string;
  boxShadows: string;
  dropShadows: string;
  link: string;
  hoverFocus: string;
  bodyColor: string;
  bodyBackground: string;
  buttonColor: string;
  buttonBackground: string;
  buttonBorder: string;
  fontFamilySansSerif: string;
  fontFamilyMonospace: string;
  h1Size: string;
  h1Color: string;
  h2Size: string;
  h2Color: string;
  h3Size: string;
  h3Color: string;
  starterTheme: string;
}

@Component({
  selector: 'color',
  templateUrl: './color.component.html',
  styleUrls: ['./color.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ColorComponent implements OnChanges, OnDestroy {
  @Input() color: ColorInterface;
  @Input() showSaveApply: boolean;
  @Input() themes: ColorInterface[];
  @Output() themesEE = new EventEmitter<any>();
  @Output() colorsEE = new EventEmitter<any>();

  loading = true;

  //
  // Color Palette
  //
  shadeBase: string;
  shadeOne: string;
  shadeTwo: string;
  shadeThree: string;
  shadeFour: string;
  neutral: string;
  offsetOne: string;
  offsetTwo: string;
  offsetThree: string;
  offsetFour: string;
  accentOne: string;
  accentTwo: string;
  accentThree: string;
  accentFour: string;
  textIcons: string;
  light: string;
  lightPrimaryColor: string;
  dividerColor: string;
  primary: string;
  secondaryText: string;
  darkPrimaryColor: string;
  dark: string;
  primaryText: string;
  warning: string;
  accentColor: string;
  secondary: string;
  info: string;
  //
  // Branding Variables
  //
  headerBackground: string;
  headerColor: string;
  footerBackground: string;
  footerColor: string;
  boxShadows: string;
  dropShadows: string;
  link: string;
  hoverFocus: string;
  bodyColor: string;
  bodyBackground: string;
  buttonColor: string;
  buttonBackground: string;
  buttonBorder: string;
  fontFamilySansSerif: string;
  fontFamilyMonospace: string;
  h1Size: string;
  h1Color: string;
  h2Size: string;
  h2Color: string;
  h3Size: string;
  h3Color: string;
  //
  // Color declarations for Yahoo buttons...
  //
  colorRed: string;
  colorPink: string;
  colorPurple: string;
  colorDeepPurple: string;
  colorIndigo: string;
  colorBlue: string;
  colorLightBlue: string;
  colorCyan: string;
  colorTeal: string;
  colorGreen: string;
  colorLightGreen: string;
  colorLime: string;
  colorYellow: string;
  colorAmber: string;
  colorOrange: string;
  colorDeepOrange: string;
  colorBrown: string;
  colorGrey: string;
  colorBlueGrey: string;
  // not Angular Color Palette.
  colorLight: string;
  colorDark: string;
  colorPurpleRain: string;
  colorMidnightBlue: string;
  colorRoyal: string;
  colorBabyBlue: string;
  sephia: string;

  //
  // flags and new variables to save themes
  //

  showDesign: boolean;
  themeName: string;
  showSaveButton: boolean;
  showApplyButton: boolean;
  showResetButton: boolean;
  isEditTheme: boolean;
  isNewTheme: boolean;
  isShowApply: boolean;
  isShowEdit: boolean;
  isShowDelete: boolean;
  selectDefault: string;

  private _themeId: string;
  private _starterTheme = '';
  private _currentTheme: any;
  private _isCollapsedTheme = false;
  private _isCollapsedPalette = true;
  private _isCollapsedBrand = true;
  private _colorDefault: any = {};
  private _typeOf: string;
  private _headerSubscription: Subscription;

  constructor(
    private _colorsService: ColorsService,
    private _predefinedTemplatesService: PredefinedTemplatesService,
    private _sharedService: SharedService,
    private _storage: StorageService,
    private _snack: MatSnackBar,
    private _templateService: PredefinedTemplatesService,
  ) {
    //
    // flags and new theme variables
    //
    this.themeName = '';
    this._starterTheme = '';
    this.showDesign = false;
    this.themes = [];
    this.showSaveButton = false;
    this.showApplyButton = false;
    this.showResetButton = false;
    this.isEditTheme = false;
    this.isNewTheme = false;
    this.isShowApply = false;
    this.isShowEdit = false;
    this.isShowDelete = false;
    this.selectDefault = 'None';

    // Assign Circles to the respective Color Values.
    this.colorLight = 'linear-gradient(to bottom right, #aaa, lightgrey)';
    this.colorDark = 'linear-gradient(to bottom right, darkgrey, #454545)';
    this.colorPurpleRain = 'linear-gradient(to bottom right, #e1bee7, #311b92)';
    this.colorPurple = 'linear-gradient(to bottom right, #e1bee7, #ba68c8)'; // 300 to 800.
    this.colorMidnightBlue = 'linear-gradient(to bottom right, #3F51B5, #1a237e)';
    this.colorLightBlue = 'linear-gradient(to bottom right, #64b5f6, #1976d2)';
    this.colorBlue = 'linear-gradient(to bottom right, #64b5f6, #1976d2)';
    this.colorCyan = 'linear-gradient(to bottom right, #4dd0e1, #00838f)';
    this.colorTeal = 'linear-gradient(to bottom right, #4db6ac, #00695c)';
    this.colorGreen = 'linear-gradient(to bottom right, #81c784, #1b5e20)';
    this.colorLightGreen = 'linear-gradient(to bottom right, #aed581, #558b2f)';
    this.colorLime = 'linear-gradient(to bottom right, #dce775, #9e9d24)';
    this.colorYellow = 'linear-gradient(to bottom right, #fff176, #f9a825)';
    this.colorAmber = 'linear-gradient(to bottom right, #ffd54f, #ff8f00)';
    this.colorOrange = 'linear-gradient(to bottom right, #ffdca8, #ff9800)';
    this.colorDeepOrange = 'linear-gradient(to bottom right, #ffb74d, #e64a19)';
    this.colorRed = 'linear-gradient(to bottom right, #e57373, #7F0000)';
    this.colorPink = 'linear-gradient(to bottom right, #ffa2c3, #ad1457)';
    this.colorBrown = 'linear-gradient(to bottom right, #a1887f, #4e342e)';
    this.colorBlueGrey = 'linear-gradient(to bottom right, #90a4ae, #37474f)';
    this.colorRoyal = 'linear-gradient(to bottom right, #039be5, #1a237e)';

    // Assign Input() variable to the local varialbe for changes on the fly.. for the save.
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes) {
      if (changes.color) {
        this.showApplyButton = false;
        this.showResetButton = false;
        this.showSaveButton = false;
        if (this.isEditTheme) {
          this.showApplyButton = true;
          this.showSaveButton = true;
        }
        const color = changes.color.currentValue;
        this.color = color[0];
        this._colorDefault = color[0]; // backup to reset to defaults
        if (this.color) {
          // this would be true onlt if styles array exist in the backend
          this.getColorPalette();
        }
      }
      if (changes.themes) {
        this.themes = changes.themes.currentValue;
      }
      if (changes.showSaveApply) {
        const value = changes.showSaveApply.currentValue;
        if (value == true) {
          this.showSaveButton = true;
          this.showApplyButton = true;
        }
      }
    }
  }

  backButton() {
    this.showDesign = false;
    this.showApplyButton = false;
    this.showResetButton = false;
    this.showSaveButton = false;
    this.isShowApply = false;
    this.isShowEdit = false;
    this.isShowDelete = false;
    this.isNewTheme = false;
    this.selectDefault = 'None';
    this.removeColors();
    if (this._colorDefault) {
      this.getDefaults();
    }
  }

  deleteTheme() {
    // as for confirmation first, and then delete
    this._snack
      .open('Are you sure? ', 'Yes', {
        duration: 5000,
        verticalPosition: 'top',
      })
      .onAction()
      .subscribe(() => {
        // call the delete endpoint
        if (this._themeId) {
          const CID = this._storage.getCid();
          const LID = this._storage.getLid();
          this._templateService.deleteTheme(CID, LID, this._themeId).subscribe((response) => {
            if (response.status == '200 OK') {
              this.themes = response.data[0].themes;
              this.openSnackBar(response.message, 'ok');
              if (this._colorDefault) {
                this.getDefaults(); // since current theme is deleted
              }
              this.isShowApply = false;
              this.isShowEdit = false;
              this.isShowDelete = false;
              this.selectDefault = 'None';
            } else if (response.status == 'Fail') {
              this.openSnackBar(response.message, 'ok');
            }
          });
        } else {
          const message = 'incorrect theme id';
          this.openSnackBar(message, 'ok');
        }
      });
  }

  applyTheme() {
    // apply current theme, call main Apply function call
    this.setColorTheme();
  }

  editTheme() {
    this.isNewTheme = false;
    this.isEditTheme = true;
    // load the theme configurations
    this.showDesign = true;
    this.showApplyButton = true;
    this.showResetButton = true;
    this.showSaveButton = true;
  }

  selectOption(event: Event) {
    const value = (event.target as HTMLSelectElement).value;
    this._currentTheme = undefined;
    const string =
      value == 'Lavender' ||
      value == 'LBlue' ||
      value == 'RBlue' ||
      value == 'LGreen' ||
      value == 'OGreen' ||
      value == 'Orange' ||
      value == 'Pink' ||
      value == 'Red' ||
      value == 'Brown' ||
      value == 'BGray';
    if (value != 'none') {
      if (string) {
        // call the color theme and set it here
        this.setDropDownTheme(value);
        this.isShowApply = true;
        this.isShowEdit = false;
        this.isShowDelete = false;
      } else {
        // look for the theme with the id and get theme
        const arr = this.themes.slice();
        for (let index = 0; index < arr.length; index++) {
          const element = arr[index];
          if (element._id == value) {
            this._currentTheme = element;
            this._themeId = value;
            if (this._currentTheme.themeName) {
              this.themeName = this._currentTheme.themeName;
            }
            this.getCurrentTheme();

            this.isShowApply = true;
            this.isShowEdit = true;
            this.isShowDelete = true;
          }
        }
      }
    } else {
      this._currentTheme = undefined;
      this.isShowApply = false;
      this.isShowEdit = false;
      this.isShowDelete = false;
    }
  }

  setDropDownTheme(value) {
    switch (value) {
      case 'Lavender':
        this.setPurple();
        break;
      case 'LBlue':
        this.setLightBlue();
        break;
      case 'RBlue':
        this.setRoyal();
        break;
      case 'LGreen':
        this.setLightGreen();
        break;
      case 'OGreen':
        this.setGreen();
        break;
      case 'Orange':
        this.setOrange();
        break;
      case 'Pink':
        this.setPink();
        break;
      case 'Red':
        this.setRed();
        break;
      case 'Brown':
        this.setBrown();
        break;
      case 'BGray':
        this.setBlueGrey();
        break;
      default:
        this.setPurple();
        break;
    }
  }

  customizeTheme() {
    // show theme and cust settings and make sure they give a name to save the theme
    this.showDesign = true;
    this.themeName = '';
    this.showApplyButton = true;
    this.showSaveButton = true;
    this.showResetButton = true;
    this.isEditTheme = false;
    this.isNewTheme = true;
    this.removeColors();
    if (this._colorDefault) {
      console.log('color defaults is defined');
      console.log(this._colorDefault);
      this.getDefaults();
    } else {
      console.log('color defaults is undefined');
      console.log(this._colorDefault);
      this._currentTheme = this._colorsService.defaultColorTheme(); // get default color set in color service
      this.getCurrentTheme();
    }
  }

  saveTheme() {
    if (this.themeName == 'Lavender') {
      this.themeName = 'purple0011-0022';
    }
    if (this.themeName == 'Royal Blue') {
      this.themeName = 'royal0011-0022';
    }
    if (this.themeName == 'Light Blue') {
      this.themeName = 'lightblue0011-0022';
    }
    if (this.themeName == 'Olive Green') {
      this.themeName = 'olivegreen0011-0022';
    }
    if (this.themeName == 'Light Green') {
      this.themeName = 'lightgreen0011-0022';
    }
    if (this.themeName == 'Orange') {
      this.themeName = 'orange0011-0022';
    }
    if (this.themeName == 'Red') {
      this.themeName = 'red0011-0022';
    }
    if (this.themeName == 'Pink') {
      this.themeName = 'pink0011-0022';
    }
    if (this.themeName == 'Brown') {
      this.themeName = 'brown0011-0022';
    }
    if (this.themeName == 'Blue Gray') {
      this.themeName = 'bluegrey0011-0022';
    }

    // get the theme array and dumb it in the themes array and update the ui with new color with new colors
    //and available themes with saved themes if any
    if (/\S/.test(this.themeName) && this.themeName != null) {
      const CID = this._storage.getCid();
      const LID = this._storage.getLid();
      const theme = {
        themeName: this.themeName,
        typeOf: this._typeOf,
        shadeBase: this.shadeBase,
        shadeOne: this.shadeOne,
        shadeTwo: this.shadeTwo,
        shadeThree: this.shadeThree,
        shadeFour: this.shadeFour,
        neutral: this.neutral,
        offsetOne: this.offsetOne,
        offsetTwo: this.offsetTwo,
        offsetThree: this.offsetThree,
        offsetFour: this.offsetFour,
        accentOne: this.accentOne,
        accentTwo: this.accentTwo,
        accentThree: this.accentThree,
        accentFour: this.accentFour,
        headerBackground: this.headerBackground,
        headerColor: this.headerColor,
        footerBackground: this.footerBackground,
        footerColor: this.footerColor,
        boxShadows: this.boxShadows,
        dropShadows: this.dropShadows,
        link: this.link,
        hoverFocus: this.hoverFocus,
        bodyColor: this.bodyColor,
        bodyBackground: this.bodyBackground,
        buttonColor: this.buttonColor,
        buttonBackground: this.buttonBackground,
        buttonBorder: this.buttonBorder,
        fontFamilySansSerif: this.fontFamilySansSerif,
        fontFamilyMonospace: this.fontFamilyMonospace,
        h1Size: this.h1Size,
        h1Color: this.h1Color,
        h2Size: this.h2Size,
        h2Color: this.h2Color,
        h3Size: this.h3Size,
        h3Color: this.h3Color,
      };

      if (!this.isEditTheme) {
        // this is a new
        const postbody = {
          cid: CID,
          lid: LID,
          theme: theme,
        };

        // ---------------------Add Color Service Call--------------------------------
        this._templateService.setTheme(postbody).subscribe((response) => {
          if (response.status === '200 OK') {
            const message = response.message;
            this.themes = response.data[0].themes; // update the avaialalble themes array
            this.themesEE.emit(this.themes);
            this.openSnackBar(message, 'ok');
          }
        });
      } else if (this.isEditTheme) {
        const postbody = {
          cid: CID,
          lid: LID,
          libId: this._storage.getItem('libId'),
          themeId: this._currentTheme._id,
          theme: theme,
        };
        this._templateService
          .updateTheme(postbody)

          .subscribe((response) => {
            if (response.status == '200 OK') {
              const message = response.message;
              this.themes = response.data.themes;
              this.themesEE.emit(this.themes);
              this.openSnackBar(message, 'ok');
            }
          });
      }
      // document.body.className = '';
      // $('body').addClass(this.typeOf);
    } else {
      const message = 'Enter a theme name';
      this.openSnackBar(message, 'ok');
    }
  }

  getCurrentTheme() {
    this._starterTheme = this._currentTheme.starterTheme;
    this._typeOf = this._currentTheme.typeOf;
    this.shadeBase = this._currentTheme.shadeBase;
    this.shadeOne = this._currentTheme.shadeOne;
    this.shadeTwo = this._currentTheme.shadeTwo;
    this.shadeThree = this._currentTheme.shadeThree;
    this.shadeFour = this._currentTheme.shadeFour;
    this.neutral = this._currentTheme.neutral;
    this.offsetOne = this._currentTheme.offsetOne;
    this.offsetTwo = this._currentTheme.offsetTwo;
    this.offsetThree = this._currentTheme.offsetThree;
    this.offsetFour = this._currentTheme.offsetFour;
    this.accentOne = this._currentTheme.accentOne;
    this.accentOne = this._currentTheme.accentTwo;
    this.accentThree = this._currentTheme.accentThree;
    this.accentFour = this._currentTheme.accentFour;
    this.headerBackground = this._currentTheme.headerBackground;
    this.headerColor = this._currentTheme.headerColor;
    this.footerBackground = this._currentTheme.footerBackground;
    this.footerColor = this._currentTheme.footerColor;
    this.boxShadows = this._currentTheme.boxShadows;
    this.dropShadows = this._currentTheme.dropShadows;
    this.link = this._currentTheme.link;
    this.hoverFocus = this._currentTheme.hoverFocus;
    this.bodyColor = this._currentTheme.bodyColor;
    this.bodyBackground = this._currentTheme.bodyBackground;
    this.buttonColor = this._currentTheme.buttonColor;
    this.buttonBackground = this._currentTheme.buttonBackground;
    this.buttonBorder = this._currentTheme.buttonBorder;
    this.fontFamilySansSerif = this._currentTheme.fontFamilySansSerif;
    this.fontFamilyMonospace = this._currentTheme.fontFamilyMonospace;
    this.h1Size = this._currentTheme.h1Size;
    this.h1Color = this._currentTheme.h1Color;
    this.h2Size = this._currentTheme.h2Size;
    this.h2Color = this._currentTheme.h2Color;
    this.h3Size = this._currentTheme.h3Size;
    this.h3Color = this._currentTheme.h3Color;

    if (this._currentTheme) {
      if (this._currentTheme.themeName == 'purple0011-0022') {
        this.themeName = 'Lavender';
      }
      if (this._currentTheme.themeName == 'royal0011-0022') {
        this.themeName = 'Royal Blue';
      }
      if (this._currentTheme.themeName == 'lightblue0011-0022') {
        this.themeName = 'Light Blue';
      }
      if (this._currentTheme.themeName == 'olivegreen0011-0022') {
        this.themeName = 'Olive Green';
      }
      if (this._currentTheme.themeName == 'lightgreen0011-0022') {
        this.themeName = 'Light Green';
      }
      if (this._currentTheme.themeName == 'orange0011-0022') {
        this.themeName = 'Orange';
      }
      if (this._currentTheme.themeName == 'red0011-0022') {
        this.themeName = 'Red';
      }
      if (this._currentTheme.themeName == 'pink0011-0022') {
        this.themeName = 'Pink';
      }
      if (this._currentTheme.themeName == 'brown0011-0022') {
        this.themeName = 'Brown';
      }
      if (this._currentTheme.themeName == 'bluegrey0011-0022') {
        this.themeName = 'Blue Gray';
      }
    }

    // reset colors on the screen as well
    this.resetColors();
  }

  getColorPalette() {
    this.themeName = this.color.themeName;
    this._starterTheme = this.color.starterTheme;
    this._typeOf = this.color.typeOf;
    this.shadeBase = this.color.shadeBase;
    this.shadeOne = this.color.shadeOne;
    this.shadeTwo = this.color.shadeTwo;
    this.shadeThree = this.color.shadeThree;
    this.shadeFour = this.color.shadeFour;
    this.neutral = this.color.neutral;
    this.offsetOne = this.color.offsetOne;
    this.offsetTwo = this.color.offsetTwo;
    this.offsetThree = this.color.offsetThree;
    this.offsetFour = this.color.offsetFour;
    this.accentOne = this.color.accentOne;
    this.accentOne = this.color.accentTwo;
    this.accentThree = this.color.accentThree;
    this.accentFour = this.color.accentFour;
    this.headerBackground = this.color.headerBackground;
    this.headerColor = this.color.headerColor;
    this.footerBackground = this.color.footerBackground;
    this.footerColor = this.color.footerColor;
    this.boxShadows = this.color.boxShadows;
    this.dropShadows = this.color.dropShadows;
    this.link = this.color.link;
    this.hoverFocus = this.color.hoverFocus;
    this.bodyColor = this.color.bodyColor;
    this.bodyBackground = this.color.bodyBackground;
    this.buttonColor = this.color.buttonColor;
    this.buttonBackground = this.color.buttonBackground;
    this.buttonBorder = this.color.buttonBorder;
    this.fontFamilySansSerif = this.color.fontFamilySansSerif;
    this.fontFamilyMonospace = this.color.fontFamilyMonospace;
    this.h1Size = this.color.h1Size;
    this.h1Color = this.color.h1Color;
    this.h2Size = this.color.h2Size;
    this.h2Color = this.color.h2Color;
    this.h3Size = this.color.h3Size;
    this.h3Color = this.color.h3Color;

    if (this.color) {
      if (this.color.themeName == 'purple0011-0022') {
        this.themeName = 'Lavender';
      }
      if (this.color.themeName == 'royal0011-0022') {
        this.themeName = 'Royal Blue';
      }
      if (this.color.themeName == 'lightblue0011-0022') {
        this.themeName = 'Light Blue';
      }
      if (this.color.themeName == 'olivegreen0011-0022') {
        this.themeName = 'Olive Green';
      }
      if (this.color.themeName == 'lightgreen0011-0022') {
        this.themeName = 'Light Green';
      }
      if (this.color.themeName == 'orange0011-0022') {
        this.themeName = 'Orange';
      }
      if (this.color.themeName == 'red0011-0022') {
        this.themeName = 'Red';
      }
      if (this.color.themeName == 'pink0011-0022') {
        this.themeName = 'Pink';
      }
      if (this.color.themeName == 'brown0011-0022') {
        this.themeName = 'Brown';
      }
      if (this.color.themeName == 'bluegrey0011-0022') {
        this.themeName = 'Blue Gray';
      }
    }

    // reset colors on the screen as well
  }
  setCustomColor(type, color) {
    document.documentElement.style.removeProperty(type);
    document.documentElement.style.setProperty(type, color);
  }
  openTheme() {
    if (this._isCollapsedTheme == true) {
      this._isCollapsedTheme = false;
      this._isCollapsedPalette = true;
      this._isCollapsedBrand = true;
    } else {
      this._isCollapsedTheme = true;
    }
  }
  openPalette() {
    if (this._isCollapsedPalette == true) {
      this._isCollapsedTheme = true;
      this._isCollapsedPalette = false;
      this._isCollapsedBrand = true;
    } else {
      this._isCollapsedPalette = true;
    }
  }
  openBranding() {
    if (this._isCollapsedBrand == true) {
      this._isCollapsedTheme = true;
      this._isCollapsedPalette = true;
      this._isCollapsedBrand = false;
    } else {
      this._isCollapsedBrand = true;
    }
  }
  removeColors() {
    document.body.className = '';
    //
    // Color Theme
    this.themeName = '';
    this._starterTheme = '';
    document.documentElement.style.removeProperty('--shade-base');
    document.documentElement.style.removeProperty('--shade-one');
    document.documentElement.style.removeProperty('--shade-two');
    document.documentElement.style.removeProperty('--shade-three');
    document.documentElement.style.removeProperty('--shade-four');
    document.documentElement.style.removeProperty('--neutral'); // for custom, material, and bootstrap.
    document.documentElement.style.removeProperty('--offset-one');
    document.documentElement.style.removeProperty('--secondary-text');
    document.documentElement.style.removeProperty('--offset-two');
    document.documentElement.style.removeProperty('--dark-primary-color');
    document.documentElement.style.removeProperty('--offset-three');
    document.documentElement.style.removeProperty('--offset-four');
    document.documentElement.style.removeProperty('--primary-text');
    document.documentElement.style.removeProperty('--accent-one');
    document.documentElement.style.removeProperty('--accent-two');
    document.documentElement.style.removeProperty('--accent-three');
    document.documentElement.style.removeProperty('--accent-four');

    document.documentElement.style.removeProperty('--text-icons');
    document.documentElement.style.removeProperty('--light-primary-color');
    document.documentElement.style.removeProperty('--divider-color');

    document.documentElement.style.removeProperty('--button-color');
    document.documentElement.style.removeProperty('--button-background');
    document.documentElement.style.removeProperty('--body-color');
    document.documentElement.style.removeProperty('--body-background');
    document.documentElement.style.removeProperty('--header-background'); // offset-four
    document.documentElement.style.removeProperty('--header-color'); // TEXT/ICONS
    document.documentElement.style.removeProperty('--footer-background'); // DARK PRIMARY COLOR
    document.documentElement.style.removeProperty('--footer-color'); // TEXT/ICONS
    document.documentElement.style.removeProperty('--box-shadows'); // dark
    document.documentElement.style.removeProperty('--drop-shadows'); // dark
    document.documentElement.style.removeProperty('--link'); //PRIMARY COLOR
    document.documentElement.style.removeProperty('--hover-focus'); //ACCENT COLOR
    document.documentElement.style.removeProperty('--bs-primary'); // MAIN COLOR. DEFAULT FOR BUTTONS AS WELL.
    document.documentElement.style.removeProperty('--bs-secondary'); // ACCENT COLOR
    document.documentElement.style.removeProperty('--bs-info'); // LIGHT PRIMARY COLOR
    document.documentElement.style.removeProperty('--bs-warning'); // DEEP ORANGE
    document.documentElement.style.removeProperty('--bs-danger'); // DEEP RED
    document.documentElement.style.removeProperty('--bs-light'); // SECONDARY TEXT
    document.documentElement.style.removeProperty('--bs-dark'); // PRIMARY TEXT
  }

  setColorTheme() {
    if (this.themeName == 'Lavender') {
      this.themeName = 'purple0011-0022';
    }
    if (this.themeName == 'Royal Blue') {
      this.themeName = 'royal0011-0022';
    }
    if (this.themeName == 'Light Blue') {
      this.themeName = 'lightblue0011-0022';
    }
    if (this.themeName == 'Olive Green') {
      this.themeName = 'olivegreen0011-0022';
    }
    if (this.themeName == 'Light Green') {
      this.themeName = 'lightgreen0011-0022';
    }
    if (this.themeName == 'Orange') {
      this.themeName = 'orange0011-0022';
    }
    if (this.themeName == 'Red') {
      this.themeName = 'red0011-0022';
    }
    if (this.themeName == 'Pink') {
      this.themeName = 'pink0011-0022';
    }
    if (this.themeName == 'Brown') {
      this.themeName = 'brown0011-0022';
    }
    if (this.themeName == 'Blue Gray') {
      this.themeName = 'bluegrey0011-0022';
    }
    const CID = this._storage.getCid();
    const LID = this._storage.getLid();
    const postbody = {
      cid: CID,
      lid: LID,
      styles: [
        {
          // 'themeName':this.themeName,
          typeOf: this._typeOf,
          shadeBase: this.shadeBase,
          shadeOne: this.shadeOne,
          shadeTwo: this.shadeTwo,
          shadeThree: this.shadeThree,
          shadeFour: this.shadeFour,
          neutral: this.neutral,
          offsetOne: this.offsetOne,
          offsetTwo: this.offsetTwo,
          offsetThree: this.offsetThree,
          offsetFour: this.offsetFour,
          accentOne: this.accentOne,
          accentTwo: this.accentTwo,
          accentThree: this.accentThree,
          accentFour: this.accentFour,
          headerBackground: this.headerBackground,
          headerColor: this.headerColor,
          footerBackground: this.footerBackground,
          footerColor: this.footerColor,
          boxShadows: this.boxShadows,
          dropShadows: this.dropShadows,
          link: this.link,
          hoverFocus: this.hoverFocus,
          bodyColor: this.bodyColor,
          bodyBackground: this.bodyBackground,
          buttonColor: this.buttonColor,
          buttonBackground: this.buttonBackground,
          buttonBorder: this.buttonBorder,
          fontFamilySansSerif: this.fontFamilySansSerif,
          fontFamilyMonospace: this.fontFamilyMonospace,
          h1Size: this.h1Size,
          h1Color: this.h1Color,
          h2Size: this.h2Size,
          h2Color: this.h2Color,
          h3Size: this.h3Size,
          h3Color: this.h3Color,
          starterTheme: this._starterTheme,
          themeName: this.themeName ? this.themeName : '',
        },
      ],
    };
    // ---------------------Add Color Service Call--------------------------------
    this._templateService.saveHeader(postbody).subscribe((response) => {
      if (response.status === '200 OK') {
        const message = 'Color Theme Updated';
        const layoutSettings = response.data[0];
        this.colorsEE.emit(layoutSettings.styles); // dont emit styles[0] as it is repected in ngOnchanges
        this.color = layoutSettings.styles[0];
        if (this.color) {
          this.getColorPalette(); // bind new variables to the colors
        }
        this._colorDefault = this.color; // keep a copy to set defaults again.
        this.openSnackBar(message, 'ok');
        // get latest header and udpate the session
        this._storage.setItem('layoutSettings', layoutSettings);
        this._sharedService.setLayoutSettings(layoutSettings);
      }
    });
  }
  getDefaults() {
    this.themeName = this._colorDefault.themeName ? this._colorDefault.themeName : '';
    this._starterTheme = this._colorDefault.starterTheme;
    this._typeOf = this._colorDefault.typeOf;
    this.shadeBase = this._colorDefault.shadeBase;
    this.shadeOne = this._colorDefault.shadeOne;
    this.shadeTwo = this._colorDefault.shadeTwo;
    this.shadeThree = this._colorDefault.shadeThree;
    this.shadeFour = this._colorDefault.shadeFour;
    this.neutral = this._colorDefault.neutral;
    this.offsetOne = this._colorDefault.offsetOne;
    this.offsetTwo = this._colorDefault.offsetTwo;
    this.offsetThree = this._colorDefault.offsetThree;
    this.offsetFour = this._colorDefault.offsetFour;
    this.accentOne = this._colorDefault.accentOne;
    this.accentOne = this._colorDefault.accentTwo;
    this.accentThree = this._colorDefault.accentThree;
    this.accentFour = this._colorDefault.accentFour;
    this.headerBackground = this._colorDefault.headerBackground;
    this.headerColor = this._colorDefault.headerColor;
    this.footerBackground = this._colorDefault.footerBackground;
    this.footerColor = this._colorDefault.footerColor;
    this.boxShadows = this._colorDefault.boxShadows;
    this.dropShadows = this._colorDefault.dropShadows;
    this.link = this._colorDefault.link;
    this.hoverFocus = this._colorDefault.hoverFocus;
    this.bodyColor = this._colorDefault.bodyColor;
    this.bodyBackground = this._colorDefault.bodyBackground;
    this.buttonColor = this._colorDefault.buttonColor;
    this.buttonBackground = this._colorDefault.buttonBackground;
    this.buttonBorder = this._colorDefault.buttonBorder;
    this.fontFamilySansSerif = this._colorDefault.fontFamilySansSerif;
    this.fontFamilyMonospace = this._colorDefault.fontFamilyMonospace;
    this.h1Size = this._colorDefault.h1Size;
    this.h1Color = this._colorDefault.h1Color;
    this.h2Size = this._colorDefault.h2Size;
    this.h2Color = this._colorDefault.h2Color;
    this.h3Size = this._colorDefault.h3Size;
    this.h3Color = this._colorDefault.h3Color;

    if (this._colorDefault) {
      if (this._colorDefault.themeName == 'purple0011-0022') {
        this.themeName = 'Lavender';
      }
      if (this._colorDefault.themeName == 'royal0011-0022') {
        this.themeName = 'Royal Blue';
      }
      if (this._colorDefault.themeName == 'lightblue0011-0022') {
        this.themeName = 'Light Blue';
      }
      if (this._colorDefault.themeName == 'olivegreen0011-0022') {
        this.themeName = 'Olive Green';
      }
      if (this._colorDefault.themeName == 'lightgreen0011-0022') {
        this.themeName = 'Light Green';
      }
      if (this._colorDefault.themeName == 'orange0011-0022') {
        this.themeName = 'Orange';
      }
      if (this._colorDefault.themeName == 'red0011-0022') {
        this.themeName = 'Red';
      }
      if (this._colorDefault.themeName == 'pink0011-0022') {
        this.themeName = 'Pink';
      }
      if (this._colorDefault.themeName == 'brown0011-0022') {
        this.themeName = 'Brown';
      }
      if (this._colorDefault.themeName == 'bluegrey0011-0022') {
        this.themeName = 'Blue Gray';
      }
    }

    // this.removeColors(); // removing colors
    this.resetColors(); // reset to default ones
  }

  resetColors() {
    // newly added
    document.documentElement.style.removeProperty('--shade-base');
    document.documentElement.style.removeProperty('--shade-one');
    document.documentElement.style.removeProperty('--shade-two');
    document.documentElement.style.removeProperty('--shade-three');
    document.documentElement.style.removeProperty('--shade-four');
    document.documentElement.style.removeProperty('--neutral'); // for custom, material, and bootstrap.
    document.documentElement.style.removeProperty('--offset-one');
    document.documentElement.style.removeProperty('--offset-two');
    document.documentElement.style.removeProperty('--offset-three'); // text-icons
    document.documentElement.style.removeProperty('--offset-four');
    document.documentElement.style.removeProperty('--accent-one'); // text-icons
    document.documentElement.style.removeProperty('--accent-two');
    document.documentElement.style.removeProperty('--accent-three');
    document.documentElement.style.removeProperty('--accent-four'); // text-icons

    document.documentElement.style.removeProperty('--text-icons');
    document.documentElement.style.removeProperty('--primary-text');
    document.documentElement.style.removeProperty('--light');
    document.documentElement.style.removeProperty('--light-primary-color');
    document.documentElement.style.removeProperty('--dark');
    document.documentElement.style.removeProperty('--dark-primary-color');
    document.documentElement.style.removeProperty('--secondary-text');
    document.documentElement.style.removeProperty('--divider-color');
    document.documentElement.style.removeProperty('--warning');
    document.documentElement.style.removeProperty('--secondary');
    document.documentElement.style.removeProperty('--info');

    // Primary Colors

    document.documentElement.style.removeProperty('--header-color');
    document.documentElement.style.removeProperty('--header-background');
    document.documentElement.style.removeProperty('--footer-color');
    document.documentElement.style.removeProperty('--footer-background');
    document.documentElement.style.removeProperty('--body-color');
    document.documentElement.style.removeProperty('--body-background');
    document.documentElement.style.removeProperty('--link');
    document.documentElement.style.removeProperty('--hover-focus');
    document.documentElement.style.removeProperty('--button-color');
    document.documentElement.style.removeProperty('--button-background');
    document.documentElement.style.removeProperty('--button-border');
    document.documentElement.style.removeProperty('--neutral');
    document.documentElement.style.removeProperty('--offset-one');
    document.documentElement.style.removeProperty('--offset-two');

    document.documentElement.style.setProperty('--header-color', this.headerColor);
    document.documentElement.style.setProperty('--header-background', this.headerBackground);
    document.documentElement.style.setProperty('--footer-color', this.footerColor);
    document.documentElement.style.setProperty('--footer-background', this.footerBackground);
    document.documentElement.style.setProperty('--body-color', this.bodyColor);
    document.documentElement.style.setProperty('--body-background', this.bodyBackground);
    document.documentElement.style.setProperty('--link', this.link);
    document.documentElement.style.setProperty('--hover-focus', this.hoverFocus);
    document.documentElement.style.setProperty('--button-color', this.buttonColor);
    document.documentElement.style.setProperty('--button-background', this.buttonBackground);
    document.documentElement.style.setProperty('--button-border', this.buttonBorder);
    document.documentElement.style.setProperty('--neutral', this.neutral);
    document.documentElement.style.setProperty('--offset-one', this.offsetOne);
    document.documentElement.style.setProperty('--offset-two', this.offsetTwo);
  }

  setLight() {
    // default settings.
    this.removeColors();
    this._starterTheme = 'light';
    this._typeOf = 'mat-light';
    this.shadeBase = '#333';
    this.shadeOne = '#212121';
    this.shadeTwo = '#424242';
    this.shadeThree = '#616161';
    this.shadeFour = '#757575';
    this.neutral = '#9e9e9e';
    this.offsetOne = '#bdbdbd';
    this.offsetTwo = '#e0e0e0';
    this.offsetThree = '#eeeeee';
    this.offsetFour = '#fafafa';
    this.accentOne = '#8c9eff';
    this.accentTwo = '#536dfe';
    this.accentThree = '#3d5afe';
    this.accentFour = '#304ffe';

    // set Local Variables
    this.headerBackground = '#fafafa';
    this.headerColor = '#212121';
    this.footerBackground = '#fafafa';
    this.footerColor = '#212121';
    this.boxShadows = '#212121';
    this.dropShadows = '#212121';
    this.link = '#303650';
    this.hoverFocus = '#111d50';
    this.bodyColor = '#212121';
    this.bodyBackground = '#fafafa';
    this.buttonColor = '#fafafa';
    this.buttonBackground = '#111d50';
    this.buttonBorder = '#111d50';
    this.fontFamilySansSerif = ' ';
    this.fontFamilyMonospace = ' ';
    this.h1Size = '21px';
    this.h1Color = '#212121';
    this.h2Size = '#18px';
    this.h2Color = '#212121';
    this.h3Size = '#15px';
    this.h3Color = '#212121';
    // Primary Colors
    document.documentElement.style.setProperty('--shade-base', this.shadeBase);
    document.documentElement.style.setProperty('--shade-one', this.shadeOne);
    document.documentElement.style.setProperty('--shade-two', this.shadeTwo);
    document.documentElement.style.setProperty('--shade-three', this.shadeThree);
    document.documentElement.style.setProperty('--shade-four', this.shadeFour);
    document.documentElement.style.setProperty('--neutral', this.neutral);
    document.documentElement.style.setProperty('--offset-one', this.offsetOne);
    document.documentElement.style.setProperty('--offset-two', this.offsetTwo);
    document.documentElement.style.setProperty('--offset-three', this.offsetThree);
    document.documentElement.style.setProperty('--offset-four', this.offsetFour);
    // Accent Colors
    document.documentElement.style.setProperty('--accent-one', this.accentOne);
    document.documentElement.style.setProperty('--accent-two', this.accentTwo);
    document.documentElement.style.setProperty('--accent-three', this.accentThree);
    document.documentElement.style.setProperty('--accent-four', this.offsetFour);
    // Angular
    document.documentElement.style.setProperty('--text-icons', this.shadeBase); // shade base
    document.documentElement.style.setProperty('--primary-text', this.shadeOne); // shade one
    document.documentElement.style.setProperty('--dark', this.shadeTwo); // shade two
    document.documentElement.style.setProperty('--dark-primary-color', this.shadeThree); // shade three
    document.documentElement.style.setProperty('--secondary-text', this.offsetOne); // offset one
    document.documentElement.style.setProperty('--divider-color', this.offsetTwo); // offset two
    document.documentElement.style.setProperty('--light-primary-color', this.offsetThree); // offset three
    document.documentElement.style.setProperty('--light', this.offsetFour); // offset four
    document.documentElement.style.setProperty('--warning', this.accentOne); // accent one
    document.documentElement.style.setProperty('--secondary', this.accentTwo); // accent two
    document.documentElement.style.setProperty('--info', this.accentFour); // accent four
    //Bootstrap
    document.documentElement.style.setProperty('--bs-primary', this.neutral); // neutral
    document.documentElement.style.setProperty('--bs-secondary', this.offsetOne); // accent two
    document.documentElement.style.setProperty('--bs-info', this.offsetOne); // offset three
    document.documentElement.style.setProperty('--bs-warning', this.offsetOne); // DEEP ORANGE
    document.documentElement.style.setProperty('--bs-danger', this.offsetOne); // DEEP RED
    document.documentElement.style.setProperty('--bs-light', this.shadeTwo); // --shade-two
    document.documentElement.style.setProperty('--bs-dark', this.offsetTwo); // --offset-two
    // Custom Defined
    document.documentElement.style.setProperty('--header-background', this.headerBackground);
    document.documentElement.style.setProperty('--header-color', this.headerColor);
    document.documentElement.style.setProperty('--footer-background', this.footerBackground);
    document.documentElement.style.setProperty('--footer-color', this.footerColor);
    document.documentElement.style.setProperty('--body-background', this.bodyBackground);
    document.documentElement.style.setProperty('--body-color', this.bodyColor);
    document.documentElement.style.setProperty('--button-background', this.buttonBackground);
    document.documentElement.style.setProperty('--button-border', this.buttonBorder);
    document.documentElement.style.setProperty('--button-color', this.buttonColor);
    document.documentElement.style.setProperty('--box-shadows', this.boxShadows);
    document.documentElement.style.setProperty('--drop-shadows', this.dropShadows);
    document.documentElement.style.setProperty('--link', this.link);
    document.documentElement.style.setProperty('--hover-focus', this.hoverFocus);
    document.body.className = '';
    $('body').addClass(this._typeOf);
  }

  setDark() {
    this.removeColors();
    this._starterTheme = 'dark';
    this._typeOf = 'mat-dark';
    this.shadeBase = '#fafafa';
    this.shadeOne = '#f5f5f5';
    this.shadeTwo = '#eeeeee';
    this.shadeThree = '#e0e0e0';
    this.shadeFour = '#bdbdbd';
    this.neutral = '#9e9e9e';
    this.offsetOne = '#757575';
    this.offsetTwo = '#616161';
    this.offsetThree = '#424242';
    this.offsetFour = '#212121';
    this.accentOne = '#8c9eff';
    this.accentTwo = '#536dfe';
    this.accentThree = '#3d5afe';
    this.accentFour = '#304ffe';
    // set Local Variables
    this.headerBackground = '#212121';
    this.headerColor = '#fafafa';
    this.footerBackground = '#212121';
    this.footerColor = '#fafafa';
    this.boxShadows = '#212121';
    this.dropShadows = '#212121';
    this.link = '#1ab6ff';
    this.hoverFocus = '#40c4ff';
    this.bodyColor = '#fafafa';
    this.bodyBackground = '#333';
    this.buttonColor = '#fafafa';
    this.buttonBackground = '#0091ea';
    this.buttonBorder = '#0091ea';
    this.fontFamilySansSerif = ' ';
    this.fontFamilyMonospace = ' ';
    this.h1Size = '21px';
    this.h1Color = '#fafafa';
    this.h2Size = '#18px';
    this.h2Color = '#fafafa';
    this.h3Size = '#15px';
    this.h3Color = '#fafafa';
    // Primary Colors
    document.documentElement.style.setProperty('--shade-base', this.shadeBase);
    document.documentElement.style.setProperty('--shade-one', this.shadeOne);
    document.documentElement.style.setProperty('--shade-two', this.shadeTwo);
    document.documentElement.style.setProperty('--shade-three', this.shadeThree);
    document.documentElement.style.setProperty('--shade-four', this.shadeFour);
    document.documentElement.style.setProperty('--neutral', this.neutral);
    document.documentElement.style.setProperty('--offset-one', this.offsetOne);
    document.documentElement.style.setProperty('--offset-two', this.offsetTwo);
    document.documentElement.style.setProperty('--offset-three', this.offsetThree);
    document.documentElement.style.setProperty('--offset-four', this.offsetFour);
    // Accent Colors
    document.documentElement.style.setProperty('--accent-one', this.accentOne);
    document.documentElement.style.setProperty('--accent-two', this.accentTwo);
    document.documentElement.style.setProperty('--accent-three', this.accentThree);
    document.documentElement.style.setProperty('--accent-four', this.offsetFour);
    // Angular
    document.documentElement.style.setProperty('--text-icons', this.shadeBase); // shade base
    document.documentElement.style.setProperty('--primary-text', this.shadeOne); // shade one
    document.documentElement.style.setProperty('--dark', this.shadeTwo); // shade two
    document.documentElement.style.setProperty('--dark-primary-color', this.shadeThree); // shade three
    document.documentElement.style.setProperty('--secondary-text', this.offsetOne); // offset one
    document.documentElement.style.setProperty('--divider-color', this.offsetTwo); // offset two
    document.documentElement.style.setProperty('--light-primary-color', this.offsetThree); // offset three
    document.documentElement.style.setProperty('--light', this.offsetFour); // offset four
    document.documentElement.style.setProperty('--warning', this.accentOne); // accent one
    document.documentElement.style.setProperty('--secondary', this.accentTwo); // accent two
    document.documentElement.style.setProperty('--info', this.accentFour); // accent four
    //Bootstrap
    document.documentElement.style.setProperty('--bs-primary', this.neutral); // neutral
    document.documentElement.style.setProperty('--bs-secondary', this.offsetOne); // accent two
    document.documentElement.style.setProperty('--bs-info', this.offsetOne); // offset three
    document.documentElement.style.setProperty('--bs-warning', this.offsetOne); // DEEP ORANGE
    document.documentElement.style.setProperty('--bs-danger', this.offsetOne); // DEEP RED
    document.documentElement.style.setProperty('--bs-light', this.shadeTwo); // --shade-two
    document.documentElement.style.setProperty('--bs-dark', this.offsetTwo); // --offset-two
    // Custom Defined
    document.documentElement.style.setProperty('--header-background', this.headerBackground);
    document.documentElement.style.setProperty('--header-color', this.headerColor);
    document.documentElement.style.setProperty('--footer-background', this.footerBackground);
    document.documentElement.style.setProperty('--footer-color', this.footerColor);
    document.documentElement.style.setProperty('--body-background', this.bodyBackground);
    document.documentElement.style.setProperty('--body-color', this.bodyColor);
    document.documentElement.style.setProperty('--button-background', this.buttonBackground);
    document.documentElement.style.setProperty('--button-border', this.buttonBorder);
    document.documentElement.style.setProperty('--button-color', this.buttonColor);
    document.documentElement.style.setProperty('--box-shadows', this.boxShadows);
    document.documentElement.style.setProperty('--drop-shadows', this.dropShadows);
    document.documentElement.style.setProperty('--link', this.link);
    document.documentElement.style.setProperty('--hover-focus', this.hoverFocus);
    document.body.className = '';
    $('body').addClass(this._typeOf);
  }

  setPurple() {
    this.removeColors();
    this.themeName = 'Lavender';
    this._starterTheme = 'purple';
    this._typeOf = 'mat-purple';
    this.shadeBase = '#f3e5f5';
    this.shadeOne = '#e1bee7';
    this.shadeTwo = '#ce93d8';
    this.shadeThree = '#C681D0';
    // this.shadeFour ='#ab47bc';
    this.shadeFour = '#000000';
    this.neutral = '#E0AAEE';
    this.offsetOne = '#D16FE2';
    this.offsetTwo = '#D16FE2';
    this.offsetThree = '#D16FE2';
    this.offsetFour = '#D16FE2';
    this.accentOne = '#ea80fc';
    this.accentTwo = '#F093FB';
    this.accentThree = '#8C009E';
    this.accentFour = '#D761FF';
    // set Local Variables
    this.headerBackground = '#ead3ee';
    this.headerColor = '#000000';
    this.footerBackground = '#ead3ee';
    this.footerColor = '#000000';
    this.bodyColor = '#000000';
    this.bodyBackground = '#fef7ff';
    this.boxShadows = '#000000';
    this.dropShadows = '#000000';
    this.link = '#B185EF';
    this.hoverFocus = '#C475E6';
    this.buttonColor = '#fafafa';
    this.buttonBackground = '#CF75E1';
    this.buttonBorder = '#CF71E0';

    this.fontFamilySansSerif = '#dcedc8';
    this.fontFamilyMonospace = '#dcedc8';
    this.h1Size = '21px';
    this.h1Color = '#212121';
    this.h2Size = '#18px';
    this.h2Color = '#212121';
    this.h3Size = '#15px';
    this.h3Color = '#212121';
    // Primary Colors
    document.documentElement.style.setProperty('--shade-base', this.shadeBase);
    document.documentElement.style.setProperty('--shade-one', this.shadeOne);
    document.documentElement.style.setProperty('--shade-two', this.shadeTwo);
    document.documentElement.style.setProperty('--shade-three', this.shadeThree);
    document.documentElement.style.setProperty('--shade-four', this.shadeFour);
    document.documentElement.style.setProperty('--neutral', this.neutral);
    document.documentElement.style.setProperty('--offset-one', this.offsetOne);
    document.documentElement.style.setProperty('--offset-two', this.offsetTwo);
    document.documentElement.style.setProperty('--offset-three', this.offsetThree);
    document.documentElement.style.setProperty('--offset-four', this.offsetFour);
    // Accent Colors
    document.documentElement.style.setProperty('--accent-one', this.accentOne);
    document.documentElement.style.setProperty('--accent-two', this.accentTwo);
    document.documentElement.style.setProperty('--accent-three', this.accentThree);
    document.documentElement.style.setProperty('--accent-four', this.offsetFour);
    // Angular
    document.documentElement.style.setProperty('--text-icons', this.shadeBase); // shade base
    document.documentElement.style.setProperty('--primary-text', this.shadeOne); // shade one
    document.documentElement.style.setProperty('--dark', this.shadeTwo); // shade two
    document.documentElement.style.setProperty('--dark-primary-color', this.shadeThree); // shade three
    document.documentElement.style.setProperty('--secondary-text', this.offsetOne); // offset one
    document.documentElement.style.setProperty('--divider-color', this.offsetTwo); // offset two
    document.documentElement.style.setProperty('--light-primary-color', this.offsetThree); // offset three
    document.documentElement.style.setProperty('--light', this.offsetFour); // offset four
    document.documentElement.style.setProperty('--warning', this.accentOne); // accent one
    document.documentElement.style.setProperty('--secondary', this.accentTwo); // accent two
    document.documentElement.style.setProperty('--info', this.accentFour); // accent four
    //Bootstrap
    document.documentElement.style.setProperty('--bs-primary', this.neutral); // neutral
    document.documentElement.style.setProperty('--bs-secondary', this.offsetOne); // accent two
    document.documentElement.style.setProperty('--bs-info', this.offsetOne); // offset three
    document.documentElement.style.setProperty('--bs-warning', this.offsetOne); // DEEP ORANGE
    document.documentElement.style.setProperty('--bs-danger', this.offsetOne); // DEEP RED
    document.documentElement.style.setProperty('--bs-light', this.shadeTwo); // --shade-two
    document.documentElement.style.setProperty('--bs-dark', this.offsetTwo); // --offset-two
    // Custom Defined
    document.documentElement.style.setProperty('--header-background', this.headerBackground);
    document.documentElement.style.setProperty('--header-color', this.headerColor);
    document.documentElement.style.setProperty('--footer-background', this.footerBackground);
    document.documentElement.style.setProperty('--footer-color', this.footerColor);
    document.documentElement.style.setProperty('--body-background', this.bodyBackground);
    document.documentElement.style.setProperty('--body-color', this.bodyColor);
    document.documentElement.style.setProperty('--button-background', this.buttonBackground);
    document.documentElement.style.setProperty('--button-border', this.buttonBorder);
    document.documentElement.style.setProperty('--button-color', this.buttonColor);
    document.documentElement.style.setProperty('--box-shadows', this.boxShadows);
    document.documentElement.style.setProperty('--drop-shadows', this.dropShadows);
    document.documentElement.style.setProperty('--link', this.link);
    document.documentElement.style.setProperty('--hover-focus', this.hoverFocus);
    document.body.className = '';
    $('body').addClass(this._typeOf);
  }

  setPurpleRain() {
    this.removeColors();
    this._starterTheme = 'purplerain';
    this._typeOf = 'mat-deep-purple';
    this.shadeBase = '#311b92';
    this.shadeOne = '#d1c4e9';
    this.shadeTwo = '#b39ddb';
    this.shadeThree = '#9575cd';
    this.shadeFour = '#7e57c2';
    this.neutral = '#673ab7';
    this.offsetOne = '#5e35b1';
    this.offsetTwo = '#68588e';
    this.offsetThree = '#a591df';
    this.offsetFour = '#311b92';
    this.accentOne = '#b388ff';
    this.accentTwo = '#7c4dff';
    this.accentThree = '#651fff';
    this.accentFour = '#6200ea';
    // set Local Variables
    this.headerBackground = '#35154b';
    this.headerColor = '#fafafa';
    this.footerBackground = '#35154b';
    this.footerColor = '#fafafa';
    this.boxShadows = '#212121';
    this.dropShadows = '#212121';
    this.link = '#5e35b1';
    this.hoverFocus = '#7e57c2';
    this.bodyColor = '#212121';
    this.bodyBackground = '#FEF9F6';
    this.buttonColor = '#fafafa';
    this.buttonBackground = '#7c4dff';
    this.buttonBorder = '#7c4dff';
    this.fontFamilySansSerif = ' ';
    this.fontFamilyMonospace = ' ';
    this.h1Size = '21px';
    this.h1Color = '#212121';
    this.h2Size = '#18px';
    this.h2Color = '#212121';
    this.h3Size = '#15px';
    this.h3Color = '#212121';
    // Primary Colors
    document.documentElement.style.setProperty('--shade-base', this.shadeBase);
    document.documentElement.style.setProperty('--shade-one', this.shadeOne);
    document.documentElement.style.setProperty('--shade-two', this.shadeTwo);
    document.documentElement.style.setProperty('--shade-three', this.shadeThree);
    document.documentElement.style.setProperty('--shade-four', this.shadeFour);
    document.documentElement.style.setProperty('--neutral', this.neutral);
    document.documentElement.style.setProperty('--offset-one', this.offsetOne);
    document.documentElement.style.setProperty('--offset-two', this.offsetTwo);
    document.documentElement.style.setProperty('--offset-three', this.offsetThree);
    document.documentElement.style.setProperty('--offset-four', this.offsetFour);
    // Accent Colors
    document.documentElement.style.setProperty('--accent-one', this.accentOne);
    document.documentElement.style.setProperty('--accent-two', this.accentTwo);
    document.documentElement.style.setProperty('--accent-three', this.accentThree);
    document.documentElement.style.setProperty('--accent-four', this.offsetFour);
    // Angular
    document.documentElement.style.setProperty('--text-icons', this.shadeBase); // shade base
    document.documentElement.style.setProperty('--primary-text', this.shadeOne); // shade one
    document.documentElement.style.setProperty('--dark', this.shadeTwo); // shade two
    document.documentElement.style.setProperty('--dark-primary-color', this.shadeThree); // shade three
    document.documentElement.style.setProperty('--secondary-text', this.offsetOne); // offset one
    document.documentElement.style.setProperty('--divider-color', this.offsetTwo); // offset two
    document.documentElement.style.setProperty('--light-primary-color', this.offsetThree); // offset three
    document.documentElement.style.setProperty('--light', this.offsetFour); // offset four
    document.documentElement.style.setProperty('--warning', this.accentOne); // accent one
    document.documentElement.style.setProperty('--secondary', this.accentTwo); // accent two
    document.documentElement.style.setProperty('--info', this.accentFour); // accent four
    //Bootstrap
    document.documentElement.style.setProperty('--bs-primary', this.neutral); // neutral
    // Custom Defined
    document.documentElement.style.setProperty('--header-background', this.headerBackground);
    document.documentElement.style.setProperty('--header-color', this.headerColor);
    document.documentElement.style.setProperty('--footer-background', this.footerBackground);
    document.documentElement.style.setProperty('--footer-color', this.footerColor);
    document.documentElement.style.setProperty('--body-background', this.bodyBackground);
    document.documentElement.style.setProperty('--body-color', this.bodyColor);
    document.documentElement.style.setProperty('--button-background', this.buttonBackground);
    document.documentElement.style.setProperty('--button-border', this.buttonBorder);
    document.documentElement.style.setProperty('--button-color', this.buttonColor);
    document.documentElement.style.setProperty('--box-shadows', this.boxShadows);
    document.documentElement.style.setProperty('--drop-shadows', this.dropShadows);
    document.documentElement.style.setProperty('--link', this.link);
    document.documentElement.style.setProperty('--hover-focus', this.hoverFocus);
    document.body.className = '';
    $('body').addClass(this._typeOf);
  }

  setMidnightBlue() {
    this.removeColors();
    this._starterTheme = 'midblue';
    this._typeOf = 'mat-blue';
    this.shadeBase = '#e8eaf6';
    this.shadeOne = '#c5cae9';
    this.shadeTwo = '#9fa8da';
    this.shadeThree = '#7986cb';
    this.shadeFour = '#5c6bc0';
    this.neutral = '#3f51b5';
    this.offsetOne = '#3949ab';
    this.offsetTwo = '#303f9f';
    this.offsetThree = '#283593';
    this.offsetFour = '#1a237e';
    this.accentOne = '#8c9eff';
    this.accentTwo = '#536dfe';
    this.accentThree = '#3d5afe';
    this.accentFour = '#304ffe';
    // Primary Colors
    document.documentElement.style.setProperty('--shade-base', this.shadeBase);
    document.documentElement.style.setProperty('--shade-one', this.shadeOne);
    document.documentElement.style.setProperty('--shade-two', this.shadeTwo);
    document.documentElement.style.setProperty('--shade-three', this.shadeThree);
    document.documentElement.style.setProperty('--shade-four', this.shadeFour);
    document.documentElement.style.setProperty('--neutral', this.neutral);
    document.documentElement.style.setProperty('--offset-one', this.offsetOne);
    document.documentElement.style.setProperty('--offset-two', this.offsetTwo);
    document.documentElement.style.setProperty('--offset-three', this.offsetThree);
    document.documentElement.style.setProperty('--offset-four', this.offsetFour);
    // Accent Colors
    document.documentElement.style.setProperty('--accent-one', this.accentOne);
    document.documentElement.style.setProperty('--accent-two', this.accentTwo);
    document.documentElement.style.setProperty('--accent-three', this.accentThree);
    document.documentElement.style.setProperty('--accent-four', this.offsetFour);
    // Angular
    document.documentElement.style.setProperty('--text-icons', this.shadeBase); // shade base
    document.documentElement.style.setProperty('--primary-text', this.shadeOne); // shade one
    document.documentElement.style.setProperty('--dark', this.shadeTwo); // shade two
    document.documentElement.style.setProperty('--dark-primary-color', this.shadeThree); // shade three
    document.documentElement.style.setProperty('--secondary-text', this.offsetOne); // offset one
    document.documentElement.style.setProperty('--divider-color', this.offsetTwo); // offset two
    document.documentElement.style.setProperty('--light-primary-color', this.offsetThree); // offset three
    document.documentElement.style.setProperty('--light', this.offsetFour); // offset four
    document.documentElement.style.setProperty('--warning', this.accentOne); // accent one
    document.documentElement.style.setProperty('--secondary', this.accentTwo); // accent two
    document.documentElement.style.setProperty('--info', this.accentFour); // accent four
    //Bootstrap
    document.documentElement.style.setProperty('--bs-primary', this.neutral); // neutral
    document.documentElement.style.setProperty('--bs-secondary', this.offsetOne); // accent two
    document.documentElement.style.setProperty('--bs-info', this.offsetOne); // offset three
    document.documentElement.style.setProperty('--bs-warning', this.offsetOne); // DEEP ORANGE
    document.documentElement.style.setProperty('--bs-danger', this.offsetOne); // DEEP RED
    document.documentElement.style.setProperty('--bs-light', this.shadeTwo); // --shade-two
    document.documentElement.style.setProperty('--bs-dark', this.offsetTwo); // --offset-two
    // Custom Defined
    document.documentElement.style.setProperty('--header-background', this.headerBackground);
    document.documentElement.style.setProperty('--header-color', this.headerColor);
    document.documentElement.style.setProperty('--footer-background', this.footerBackground);
    document.documentElement.style.setProperty('--footer-color', this.footerColor);
    document.documentElement.style.setProperty('--body-background', this.bodyBackground);
    document.documentElement.style.setProperty('--body-color', this.bodyColor);
    document.documentElement.style.setProperty('--button-background', this.buttonBackground);
    document.documentElement.style.setProperty('--button-border', this.buttonBorder);
    document.documentElement.style.setProperty('--button-color', this.buttonColor);
    document.documentElement.style.setProperty('--box-shadows', this.boxShadows);
    document.documentElement.style.setProperty('--drop-shadows', this.dropShadows);
    document.documentElement.style.setProperty('--link', this.link);
    document.documentElement.style.setProperty('--hover-focus', this.hoverFocus);
    document.body.className = '';
    $('body').addClass(this._typeOf);
  }

  setBlue() {
    this.removeColors();
    this._starterTheme = 'blue';
    this._typeOf = 'mat-blue';
    this.shadeBase = '#e3f2fd';
    this.shadeOne = '#bbdefb';
    this.shadeTwo = '#90caf9';
    this.shadeThree = '#64b5f6';
    this.shadeFour = '#42a5f5';
    this.neutral = '#2196f3';
    this.offsetOne = '#1e88e5';
    this.offsetTwo = '#1976d2';
    this.offsetThree = '#1565c0';
    this.offsetFour = '#0d47a1';
    this.accentOne = '#82b1ff';
    this.accentTwo = '#448aff';
    this.accentThree = '#2979ff';
    this.accentFour = '#2962ff';
    // Primary Colors
    document.documentElement.style.setProperty('--shade-base', this.shadeBase);
    document.documentElement.style.setProperty('--shade-one', this.shadeOne);
    document.documentElement.style.setProperty('--shade-two', this.shadeTwo);
    document.documentElement.style.setProperty('--shade-three', this.shadeThree);
    document.documentElement.style.setProperty('--shade-four', this.shadeFour);
    document.documentElement.style.setProperty('--neutral', this.neutral);
    document.documentElement.style.setProperty('--offset-one', this.offsetOne);
    document.documentElement.style.setProperty('--offset-two', this.offsetTwo);
    document.documentElement.style.setProperty('--offset-three', this.offsetThree);
    document.documentElement.style.setProperty('--offset-four', this.offsetFour);
    // Accent Colors
    document.documentElement.style.setProperty('--accent-one', this.accentOne);
    document.documentElement.style.setProperty('--accent-two', this.accentTwo);
    document.documentElement.style.setProperty('--accent-three', this.accentThree);
    document.documentElement.style.setProperty('--accent-four', this.offsetFour);
    // Angular
    document.documentElement.style.setProperty('--text-icons', this.shadeBase); // shade base
    document.documentElement.style.setProperty('--primary-text', this.shadeOne); // shade one
    document.documentElement.style.setProperty('--dark', this.shadeTwo); // shade two
    document.documentElement.style.setProperty('--dark-primary-color', this.shadeThree); // shade three
    document.documentElement.style.setProperty('--secondary-text', this.offsetOne); // offset one
    document.documentElement.style.setProperty('--divider-color', this.offsetTwo); // offset two
    document.documentElement.style.setProperty('--light-primary-color', this.offsetThree); // offset three
    document.documentElement.style.setProperty('--light', this.offsetFour); // offset four
    document.documentElement.style.setProperty('--warning', this.accentOne); // accent one
    document.documentElement.style.setProperty('--secondary', this.accentTwo); // accent two
    document.documentElement.style.setProperty('--info', this.accentFour); // accent four
    //Bootstrap
    document.documentElement.style.setProperty('--bs-primary', this.neutral); // neutral
    document.documentElement.style.setProperty('--bs-secondary', this.offsetOne); // accent two
    document.documentElement.style.setProperty('--bs-info', this.offsetOne); // offset three
    document.documentElement.style.setProperty('--bs-warning', this.offsetOne); // DEEP ORANGE
    document.documentElement.style.setProperty('--bs-danger', this.offsetOne); // DEEP RED
    document.documentElement.style.setProperty('--bs-light', this.shadeTwo); // --shade-two
    document.documentElement.style.setProperty('--bs-dark', this.offsetTwo); // --offset-two
    // Custom Defined
    document.documentElement.style.setProperty('--header-background', this.headerBackground);
    document.documentElement.style.setProperty('--header-color', this.headerColor);
    document.documentElement.style.setProperty('--footer-background', this.footerBackground);
    document.documentElement.style.setProperty('--footer-color', this.footerColor);
    document.documentElement.style.setProperty('--body-background', this.bodyBackground);
    document.documentElement.style.setProperty('--body-color', this.bodyColor);
    document.documentElement.style.setProperty('--button-background', this.buttonBackground);
    document.documentElement.style.setProperty('--button-border', this.buttonBorder);
    document.documentElement.style.setProperty('--button-color', this.buttonColor);
    document.documentElement.style.setProperty('--box-shadows', this.boxShadows);
    document.documentElement.style.setProperty('--drop-shadows', this.dropShadows);
    document.documentElement.style.setProperty('--link', this.link);
    document.documentElement.style.setProperty('--hover-focus', this.hoverFocus);
    document.body.className = '';
    $('body').addClass(this._typeOf);
  }

  setLightBlue() {
    this.removeColors();
    this.themeName = 'Light Blue';
    this._starterTheme = 'lightblue';
    this._typeOf = 'mat-light-blue';
    this.shadeBase = '#e1f5fe';
    this.shadeOne = '#b3e5fc';
    this.shadeTwo = '#81d4fa';
    this.shadeThree = '#4fc3f7';
    // this.shadeFour ='#29b6f6';
    this.shadeFour = '#000000';
    this.neutral = '#03a9f4';
    this.offsetOne = '#03A3F2';
    this.offsetTwo = '#76b4d6';
    this.offsetThree = '#8bccf2';
    this.offsetFour = '#1599FE';
    this.accentOne = '#80d8ff';
    this.accentTwo = '#40c4ff';
    this.accentThree = '#005685';
    this.accentFour = '#009EFF';
    // set Local Variables
    this.headerBackground = '#cde2ec';
    this.headerColor = '#000000';
    this.footerBackground = '#cde2ec';
    this.footerColor = '#000000';
    this.bodyColor = '#000000';
    this.bodyBackground = '#fbfeff';
    this.boxShadows = '#000000';
    this.dropShadows = '#000000';
    this.link = '#149BFF';
    this.hoverFocus = '#1599FE';
    this.buttonColor = '#fafafa';
    this.buttonBackground = '#2FA3FE';
    this.buttonBorder = '#2FA3FE';

    this.fontFamilySansSerif = '';
    this.fontFamilyMonospace = '';
    this.h1Size = '21px';
    this.h1Color = '#212121';
    this.h2Size = '#18px';
    this.h2Color = '#212121';
    this.h3Size = '#15px';
    this.h3Color = '#212121';
    // Primary Colors
    document.documentElement.style.setProperty('--shade-base', this.shadeBase);
    document.documentElement.style.setProperty('--shade-one', this.shadeOne);
    document.documentElement.style.setProperty('--shade-two', this.shadeTwo);
    document.documentElement.style.setProperty('--shade-three', this.shadeThree);
    document.documentElement.style.setProperty('--shade-four', this.shadeFour);
    document.documentElement.style.setProperty('--neutral', this.neutral);
    document.documentElement.style.setProperty('--offset-one', this.offsetOne);
    document.documentElement.style.setProperty('--offset-two', this.offsetTwo);
    document.documentElement.style.setProperty('--offset-three', this.offsetThree);
    document.documentElement.style.setProperty('--offset-four', this.offsetFour);
    // Accent Colors
    document.documentElement.style.setProperty('--accent-one', this.accentOne);
    document.documentElement.style.setProperty('--accent-two', this.accentTwo);
    document.documentElement.style.setProperty('--accent-three', this.accentThree);
    document.documentElement.style.setProperty('--accent-four', this.offsetFour);
    // Angular
    document.documentElement.style.setProperty('--text-icons', this.shadeBase); // shade base
    document.documentElement.style.setProperty('--primary-text', this.shadeOne); // shade one
    document.documentElement.style.setProperty('--dark', this.shadeTwo); // shade two
    document.documentElement.style.setProperty('--dark-primary-color', this.shadeThree); // shade three
    document.documentElement.style.setProperty('--secondary-text', this.offsetOne); // offset one
    document.documentElement.style.setProperty('--divider-color', this.offsetTwo); // offset two
    document.documentElement.style.setProperty('--light-primary-color', this.offsetThree); // offset three
    document.documentElement.style.setProperty('--light', this.offsetFour); // offset four
    document.documentElement.style.setProperty('--warning', this.accentOne); // accent one
    document.documentElement.style.setProperty('--secondary', this.accentTwo); // accent two
    document.documentElement.style.setProperty('--info', this.accentFour); // accent four
    //Bootstrap
    document.documentElement.style.setProperty('--bs-primary', this.neutral); // neutral
    document.documentElement.style.setProperty('--bs-secondary', this.offsetOne); // accent two
    document.documentElement.style.setProperty('--bs-info', this.offsetOne); // offset three
    document.documentElement.style.setProperty('--bs-warning', this.offsetOne); // DEEP ORANGE
    document.documentElement.style.setProperty('--bs-danger', this.offsetOne); // DEEP RED
    document.documentElement.style.setProperty('--bs-light', this.shadeTwo); // --shade-two
    document.documentElement.style.setProperty('--bs-dark', this.offsetTwo); // --offset-two
    // Custom Defined
    document.documentElement.style.setProperty('--header-background', this.headerBackground);
    document.documentElement.style.setProperty('--header-color', this.headerColor);
    document.documentElement.style.setProperty('--footer-background', this.footerBackground);
    document.documentElement.style.setProperty('--footer-color', this.footerColor);
    document.documentElement.style.setProperty('--body-background', this.bodyBackground);
    document.documentElement.style.setProperty('--body-color', this.bodyColor);
    document.documentElement.style.setProperty('--button-background', this.buttonBackground);
    document.documentElement.style.setProperty('--button-border', this.buttonBorder);
    document.documentElement.style.setProperty('--button-color', this.buttonColor);
    document.documentElement.style.setProperty('--box-shadows', this.boxShadows);
    document.documentElement.style.setProperty('--drop-shadows', this.dropShadows);
    document.documentElement.style.setProperty('--link', this.link);
    document.documentElement.style.setProperty('--hover-focus', this.hoverFocus);
    document.body.className = '';
    $('body').addClass(this._typeOf);
  }

  setCyan() {
    this.removeColors();
    this._starterTheme = 'cyan';
    this._typeOf = 'mat-cyan';
    this.shadeBase = '#006064';
    this.shadeOne = '#b2ebf2';
    this.shadeTwo = '#80deea';
    this.shadeThree = '#4dd0e1';
    this.shadeFour = '#26c6da';
    this.neutral = '#00bcd4';
    this.offsetOne = '#006064';
    this.offsetTwo = '#276b72';
    this.offsetThree = '#00838f';
    this.offsetFour = '#006064';
    this.accentOne = '#84ffff';
    this.accentTwo = '#18ffff';
    this.accentThree = '#00e5ff';
    this.accentFour = '#00b8d4';
    // set Local Variables
    this.headerBackground = '#00838f';
    this.headerColor = '#fafafa';
    this.footerBackground = '#00838f';
    this.footerColor = '#fafafa';
    this.bodyColor = '#212121';
    this.bodyBackground = '#e0f7fa';
    this.boxShadows = '#777777';
    this.dropShadows = '#777777';
    this.link = '#006064';
    this.hoverFocus = '#00838f';
    this.buttonColor = '#fafafa';
    this.buttonBackground = '#276b72';
    this.buttonBorder = '#276b72';
    this.fontFamilySansSerif = '';
    this.fontFamilyMonospace = '';
    this.h1Size = '21px';
    this.h1Color = '#212121';
    this.h2Size = '#18px';
    this.h2Color = '#212121';
    this.h3Size = '#15px';
    this.h3Color = '#212121';
    // Primary Colors
    document.documentElement.style.setProperty('--shade-base', this.shadeBase);
    document.documentElement.style.setProperty('--shade-one', this.shadeOne);
    document.documentElement.style.setProperty('--shade-two', this.shadeTwo);
    document.documentElement.style.setProperty('--shade-three', this.shadeThree);
    document.documentElement.style.setProperty('--shade-four', this.shadeFour);
    document.documentElement.style.setProperty('--neutral', this.neutral);
    document.documentElement.style.setProperty('--offset-one', this.offsetOne);
    document.documentElement.style.setProperty('--offset-two', this.offsetTwo);
    document.documentElement.style.setProperty('--offset-three', this.offsetThree);
    document.documentElement.style.setProperty('--offset-four', this.offsetFour);
    // Accent Colors
    document.documentElement.style.setProperty('--accent-one', this.accentOne);
    document.documentElement.style.setProperty('--accent-two', this.accentTwo);
    document.documentElement.style.setProperty('--accent-three', this.accentThree);
    document.documentElement.style.setProperty('--accent-four', this.offsetFour);
    // Angular
    document.documentElement.style.setProperty('--text-icons', this.shadeBase); // shade base
    document.documentElement.style.setProperty('--primary-text', this.shadeOne); // shade one
    document.documentElement.style.setProperty('--dark', this.shadeTwo); // shade two
    document.documentElement.style.setProperty('--dark-primary-color', this.shadeThree); // shade three
    document.documentElement.style.setProperty('--secondary-text', this.offsetOne); // offset one
    document.documentElement.style.setProperty('--divider-color', this.offsetTwo); // offset two
    document.documentElement.style.setProperty('--light-primary-color', this.offsetThree); // offset three
    document.documentElement.style.setProperty('--light', this.offsetFour); // offset four
    document.documentElement.style.setProperty('--warning', this.accentOne); // accent one
    document.documentElement.style.setProperty('--secondary', this.accentTwo); // accent two
    document.documentElement.style.setProperty('--info', this.accentFour); // accent four
    //Bootstrap
    document.documentElement.style.setProperty('--bs-primary', this.neutral); // neutral
    document.documentElement.style.setProperty('--bs-secondary', this.offsetOne); // accent two
    document.documentElement.style.setProperty('--bs-info', this.offsetOne); // offset three
    document.documentElement.style.setProperty('--bs-warning', this.offsetOne); // DEEP ORANGE
    document.documentElement.style.setProperty('--bs-danger', this.offsetOne); // DEEP RED
    document.documentElement.style.setProperty('--bs-light', this.shadeTwo); // --shade-two
    document.documentElement.style.setProperty('--bs-dark', this.offsetTwo); // --offset-two
    // Custom Defined
    document.documentElement.style.setProperty('--header-background', this.headerBackground);
    document.documentElement.style.setProperty('--header-color', this.headerColor);
    document.documentElement.style.setProperty('--footer-background', this.footerBackground);
    document.documentElement.style.setProperty('--footer-color', this.footerColor);
    document.documentElement.style.setProperty('--body-background', this.bodyBackground);
    document.documentElement.style.setProperty('--body-color', this.bodyColor);
    document.documentElement.style.setProperty('--button-background', this.buttonBackground);
    document.documentElement.style.setProperty('--button-border', this.buttonBorder);
    document.documentElement.style.setProperty('--button-color', this.buttonColor);
    document.documentElement.style.setProperty('--box-shadows', this.boxShadows);
    document.documentElement.style.setProperty('--drop-shadows', this.dropShadows);
    document.documentElement.style.setProperty('--link', this.link);
    document.documentElement.style.setProperty('--hover-focus', this.hoverFocus);
    document.body.className = '';
    $('body').addClass(this._typeOf);
  }

  setTeal() {
    this.removeColors();
    this._starterTheme = 'teal';
    this._typeOf = 'mat-teal';
    this.shadeBase = '#004d40';
    this.shadeOne = '#b2dfdb';
    this.shadeTwo = '#80cbc4';
    this.shadeThree = '#4db6ac';
    this.shadeFour = '#26a69a';
    this.neutral = '#009688';
    this.offsetOne = '#00897b';
    this.offsetTwo = '#3e887f';
    this.offsetThree = '#49a498';
    this.offsetFour = '#004d40';
    this.accentOne = '#a7ffeb';
    this.accentTwo = '#64ffda';
    this.accentThree = '#1de9b6';
    this.accentFour = '#00bfa5';
    // set Local Variables
    this.headerBackground = '#1ea192';
    this.headerColor = '#fafafa';
    this.footerBackground = '#1ea192';
    this.footerColor = '#fafafa';
    this.bodyColor = '#212121';
    this.bodyBackground = '#e0f2f1';
    this.boxShadows = '#777777';
    this.dropShadows = '#777777';
    this.link = '#004d40';
    this.hoverFocus = '#00695c';
    this.buttonColor = '#fafafa';
    this.buttonBackground = '#004d40';
    this.buttonBorder = '#004d40';
    this.fontFamilySansSerif = '';
    this.fontFamilyMonospace = '';
    this.h1Size = '21px';
    this.h1Color = '#212121';
    this.h2Size = '#18px';
    this.h2Color = '#212121';
    this.h3Size = '#15px';
    this.h3Color = '#212121';
    // Primary Colors
    document.documentElement.style.setProperty('--shade-base', this.shadeBase);
    document.documentElement.style.setProperty('--shade-one', this.shadeOne);
    document.documentElement.style.setProperty('--shade-two', this.shadeTwo);
    document.documentElement.style.setProperty('--shade-three', this.shadeThree);
    document.documentElement.style.setProperty('--shade-four', this.shadeFour);
    document.documentElement.style.setProperty('--neutral', this.neutral);
    document.documentElement.style.setProperty('--offset-one', this.offsetOne);
    document.documentElement.style.setProperty('--offset-two', this.offsetTwo);
    document.documentElement.style.setProperty('--offset-three', this.offsetThree);
    document.documentElement.style.setProperty('--offset-four', this.offsetFour);
    // Accent Colors
    document.documentElement.style.setProperty('--accent-one', this.accentOne);
    document.documentElement.style.setProperty('--accent-two', this.accentTwo);
    document.documentElement.style.setProperty('--accent-three', this.accentThree);
    document.documentElement.style.setProperty('--accent-four', this.offsetFour);
    // Angular
    document.documentElement.style.setProperty('--text-icons', this.shadeBase); // shade base
    document.documentElement.style.setProperty('--primary-text', this.shadeOne); // shade one
    document.documentElement.style.setProperty('--dark', this.shadeTwo); // shade two
    document.documentElement.style.setProperty('--dark-primary-color', this.shadeThree); // shade three
    document.documentElement.style.setProperty('--secondary-text', this.offsetOne); // offset one
    document.documentElement.style.setProperty('--divider-color', this.offsetTwo); // offset two
    document.documentElement.style.setProperty('--light-primary-color', this.offsetThree); // offset three
    document.documentElement.style.setProperty('--light', this.offsetFour); // offset four
    document.documentElement.style.setProperty('--warning', this.accentOne); // accent one
    document.documentElement.style.setProperty('--secondary', this.accentTwo); // accent two
    document.documentElement.style.setProperty('--info', this.accentFour); // accent four
    //Bootstrap
    document.documentElement.style.setProperty('--bs-primary', this.neutral); // neutral
    document.documentElement.style.setProperty('--bs-secondary', this.offsetOne); // accent two
    document.documentElement.style.setProperty('--bs-info', this.offsetOne); // offset three
    document.documentElement.style.setProperty('--bs-warning', this.offsetOne); // DEEP ORANGE
    document.documentElement.style.setProperty('--bs-danger', this.offsetOne); // DEEP RED
    document.documentElement.style.setProperty('--bs-light', this.shadeTwo); // --shade-two
    document.documentElement.style.setProperty('--bs-dark', this.offsetTwo); // --offset-two
    // Custom Defined
    document.documentElement.style.setProperty('--header-background', this.headerBackground);
    document.documentElement.style.setProperty('--header-color', this.headerColor);
    document.documentElement.style.setProperty('--footer-background', this.footerBackground);
    document.documentElement.style.setProperty('--footer-color', this.footerColor);
    document.documentElement.style.setProperty('--body-background', this.bodyBackground);
    document.documentElement.style.setProperty('--body-color', this.bodyColor);
    document.documentElement.style.setProperty('--button-background', this.buttonBackground);
    document.documentElement.style.setProperty('--button-border', this.buttonBorder);
    document.documentElement.style.setProperty('--button-color', this.buttonColor);
    document.documentElement.style.setProperty('--box-shadows', this.boxShadows);
    document.documentElement.style.setProperty('--drop-shadows', this.dropShadows);
    document.documentElement.style.setProperty('--link', this.link);
    document.documentElement.style.setProperty('--hover-focus', this.hoverFocus);
    document.body.className = '';
    $('body').addClass(this._typeOf);
  }

  setGreen() {
    this.removeColors();

    // this.themeName = 'olivegreen0011-0022';
    this.themeName = 'Olive Green';
    this._starterTheme = 'olivegreen';
    this._typeOf = 'mat-green';
    this.shadeBase = '#2EAD36';
    this.shadeOne = '#c8e6c9';
    this.shadeTwo = '#a5d6a7';
    this.shadeThree = '#81c784';
    // this.shadeFour ='#245C2F';
    this.shadeFour = '#000000';
    this.neutral = '#4caf50';
    this.offsetOne = '#52B757';
    this.offsetTwo = '#4CCC6A';
    // this.offsetTwo ='#245C2F';
    this.offsetThree = '#3FB146';
    this.offsetFour = '#34C63C';
    this.accentOne = '#b9f6ca';
    this.accentTwo = '#69f0ae';
    this.accentThree = '#005726';
    this.accentFour = '#00c853';
    //set local Variables
    this.headerBackground = '#32AE4E';
    this.headerColor = '#000000';
    this.footerBackground = '#32AE4E';
    this.footerColor = '#000000';
    // this.bodyColor = '#969696';
    this.bodyColor = '#000000';
    this.bodyBackground = '#e8f5e9';
    // this.bodyBackground = '#B7DCB9';
    this.boxShadows = '#000000';
    this.dropShadows = '#000000';
    this.link = '#2EAD36';
    this.hoverFocus = '#81c784';
    this.buttonColor = '#000000';
    this.buttonBackground = '#4CCC6A';
    this.buttonBorder = '#3eaa98';

    this.fontFamilySansSerif = '';
    this.fontFamilyMonospace = '';
    this.h1Size = '21px';
    this.h1Color = '#212121';
    this.h2Size = '#18px';
    this.h2Color = '#212121';
    this.h3Size = '#15px';
    this.h3Color = '#212121';
    // Primary Colors
    document.documentElement.style.setProperty('--shade-base', this.shadeBase);
    document.documentElement.style.setProperty('--shade-one', this.shadeOne);
    document.documentElement.style.setProperty('--shade-two', this.shadeTwo);
    document.documentElement.style.setProperty('--shade-three', this.shadeThree);
    document.documentElement.style.setProperty('--shade-four', this.shadeFour);
    document.documentElement.style.setProperty('--neutral', this.neutral);
    document.documentElement.style.setProperty('--offset-one', this.offsetOne);
    document.documentElement.style.setProperty('--offset-two', this.offsetTwo);
    document.documentElement.style.setProperty('--offset-three', this.offsetThree);
    document.documentElement.style.setProperty('--offset-four', this.offsetFour);
    // Accent Colors
    document.documentElement.style.setProperty('--accent-one', this.accentOne);
    document.documentElement.style.setProperty('--accent-two', this.accentTwo);
    document.documentElement.style.setProperty('--accent-three', this.accentThree);
    document.documentElement.style.setProperty('--accent-four', this.offsetFour);
    // Angular
    document.documentElement.style.setProperty('--text-icons', this.shadeBase); // shade base
    document.documentElement.style.setProperty('--primary-text', this.shadeOne); // shade one
    document.documentElement.style.setProperty('--dark', this.shadeTwo); // shade two
    document.documentElement.style.setProperty('--dark-primary-color', this.shadeThree); // shade three
    document.documentElement.style.setProperty('--secondary-text', this.offsetOne); // offset one
    document.documentElement.style.setProperty('--divider-color', this.offsetTwo); // offset two
    document.documentElement.style.setProperty('--light-primary-color', this.offsetThree); // offset three
    document.documentElement.style.setProperty('--light', this.offsetFour); // offset four
    document.documentElement.style.setProperty('--warning', this.accentOne); // accent one
    document.documentElement.style.setProperty('--secondary', this.accentTwo); // accent two
    document.documentElement.style.setProperty('--info', this.accentFour); // accent four
    //Bootstrap
    document.documentElement.style.setProperty('--bs-primary', this.neutral); // neutral
    document.documentElement.style.setProperty('--bs-secondary', this.offsetOne); // accent two
    document.documentElement.style.setProperty('--bs-info', this.offsetOne); // offset three
    document.documentElement.style.setProperty('--bs-warning', this.offsetOne); // DEEP ORANGE
    document.documentElement.style.setProperty('--bs-danger', this.offsetOne); // DEEP RED
    document.documentElement.style.setProperty('--bs-light', this.shadeTwo); // --shade-two
    document.documentElement.style.setProperty('--bs-dark', this.offsetTwo); // --offset-two
    // Custom Defined
    document.documentElement.style.setProperty('--header-background', this.headerBackground);
    document.documentElement.style.setProperty('--header-color', this.headerColor);
    document.documentElement.style.setProperty('--footer-background', this.footerBackground);
    document.documentElement.style.setProperty('--footer-color', this.footerColor);
    document.documentElement.style.setProperty('--body-background', this.bodyBackground);
    document.documentElement.style.setProperty('--body-color', this.bodyColor);
    document.documentElement.style.setProperty('--button-background', this.buttonBackground);
    document.documentElement.style.setProperty('--button-border', this.buttonBorder);
    document.documentElement.style.setProperty('--button-color', this.buttonColor);
    document.documentElement.style.setProperty('--box-shadows', this.boxShadows);
    document.documentElement.style.setProperty('--drop-shadows', this.dropShadows);
    document.documentElement.style.setProperty('--link', this.link);
    document.documentElement.style.setProperty('--hover-focus', this.hoverFocus);
    document.body.className = '';
    $('body').addClass(this._typeOf);
  }

  setLightGreen() {
    this.removeColors();
    this.themeName = 'Light Green';
    this._starterTheme = 'lightgreen';
    this._typeOf = 'mat-light-green';
    this.shadeBase = '#33691e';
    this.shadeOne = '#dcedc8';
    this.shadeTwo = '#c5e1a5';
    this.shadeThree = '#aed581';
    this.shadeFour = '#9ccc65'; // 9ccc65
    this.neutral = '#8bc34a';
    this.offsetOne = '#33691e'; // 7cb342
    this.offsetTwo = '#c5e1a5'; // 689f38
    this.offsetThree = '#dcedc8'; // 558b2f
    this.offsetFour = '#33691e';
    this.accentOne = '#ccff90';
    this.accentTwo = '#b2ff59';
    this.accentThree = '#285C00';
    this.accentFour = '#64dd17';
    // set Local Variables
    this.headerBackground = '#dcedc8';
    this.headerColor = '#000000';
    this.footerBackground = '#dcedc8';
    this.footerColor = '#000000';
    this.bodyColor = '#000000';
    this.bodyBackground = '#f1f8e9';
    this.boxShadows = '#000000';
    this.dropShadows = '#000000';
    this.link = '#33691e';
    this.hoverFocus = '#558b2f';
    this.buttonColor = '#f1f8e9';
    this.buttonBackground = '#33691e';
    this.buttonBorder = '#33691e';

    this.fontFamilySansSerif = '#dcedc8';
    this.fontFamilyMonospace = '#dcedc8';
    this.h1Size = '21px';
    this.h1Color = '#212121';
    this.h2Size = '#18px';
    this.h2Color = '#212121';
    this.h3Size = '#15px';
    this.h3Color = '#212121';

    // Primary Colors
    document.documentElement.style.setProperty('--shade-base', this.shadeBase);
    document.documentElement.style.setProperty('--shade-one', this.shadeOne);
    document.documentElement.style.setProperty('--shade-two', this.shadeTwo);
    document.documentElement.style.setProperty('--shade-three', this.shadeThree);
    document.documentElement.style.setProperty('--shade-four', this.shadeFour);
    document.documentElement.style.setProperty('--neutral', this.neutral);
    document.documentElement.style.setProperty('--offset-one', this.offsetOne);
    document.documentElement.style.setProperty('--offset-two', this.offsetTwo);
    document.documentElement.style.setProperty('--offset-three', this.offsetThree);
    document.documentElement.style.setProperty('--offset-four', this.offsetFour);
    // Accent Colors
    document.documentElement.style.setProperty('--accent-one', this.accentOne);
    document.documentElement.style.setProperty('--accent-two', this.accentTwo);
    document.documentElement.style.setProperty('--accent-three', this.accentThree);
    document.documentElement.style.setProperty('--accent-four', this.offsetFour);
    // Angular
    document.documentElement.style.setProperty('--text-icons', this.shadeBase); // shade base
    document.documentElement.style.setProperty('--primary-text', this.shadeOne); // shade one
    document.documentElement.style.setProperty('--dark', this.shadeTwo); // shade two
    document.documentElement.style.setProperty('--dark-primary-color', this.shadeThree); // shade three
    document.documentElement.style.setProperty('--secondary-text', this.offsetOne); // offset one
    document.documentElement.style.setProperty('--divider-color', this.offsetTwo); // offset two
    document.documentElement.style.setProperty('--light-primary-color', this.offsetThree); // offset three
    document.documentElement.style.setProperty('--light', this.offsetFour); // offset four
    document.documentElement.style.setProperty('--warning', this.accentOne); // accent one
    document.documentElement.style.setProperty('--secondary', this.accentTwo); // accent two
    document.documentElement.style.setProperty('--info', this.accentFour); // accent four
    //Bootstrap
    document.documentElement.style.setProperty('--bs-primary', this.neutral); // neutral
    document.documentElement.style.setProperty('--bs-secondary', this.offsetOne); // accent two
    document.documentElement.style.setProperty('--bs-info', this.offsetOne); // offset three
    document.documentElement.style.setProperty('--bs-warning', this.offsetOne); // DEEP ORANGE
    document.documentElement.style.setProperty('--bs-danger', this.offsetOne); // DEEP RED
    document.documentElement.style.setProperty('--bs-light', this.shadeTwo); // --shade-two
    document.documentElement.style.setProperty('--bs-dark', this.offsetTwo); // --offset-two
    // Custom Defined
    document.documentElement.style.setProperty('--header-background', this.headerBackground);
    document.documentElement.style.setProperty('--header-color', this.headerColor);
    document.documentElement.style.setProperty('--footer-background', this.footerBackground);
    document.documentElement.style.setProperty('--footer-color', this.footerColor);
    document.documentElement.style.setProperty('--body-background', this.bodyBackground);
    document.documentElement.style.setProperty('--body-color', this.bodyColor);
    document.documentElement.style.setProperty('--button-background', this.buttonBackground);
    document.documentElement.style.setProperty('--button-border', this.buttonBorder);
    document.documentElement.style.setProperty('--button-color', this.buttonColor);
    document.documentElement.style.setProperty('--box-shadows', this.boxShadows);
    document.documentElement.style.setProperty('--drop-shadows', this.dropShadows);
    document.documentElement.style.setProperty('--link', this.link);
    document.documentElement.style.setProperty('--hover-focus', this.hoverFocus);
    document.body.className = '';
    $('body').addClass(this._typeOf);
  }

  setLime() {
    this.removeColors();
    this._starterTheme = 'lime';
    this._typeOf = 'mat-lime';
    this.shadeBase = '#f9fbe7';
    this.shadeOne = '#f0f4c3';
    this.shadeTwo = '#e6ee9c';
    this.shadeThree = '#dce775';
    this.shadeFour = '#d4e157';
    this.neutral = '#cddc39';
    this.offsetOne = '#c0ca33';
    this.offsetTwo = '#bce08a';
    this.offsetThree = '#adcd6b';
    this.offsetFour = '#827717';
    this.accentOne = '#f4ff81';
    this.accentTwo = '#eeff41';
    this.accentThree = '#c6ff00';
    this.accentFour = '#aeea00';
    // set Local Variables
    this.headerBackground = '#bdc8a1';
    this.headerColor = '#212121';
    this.footerBackground = '#bdc8a1';
    this.footerColor = '#212121';
    this.bodyColor = '#212121';
    this.bodyBackground = '#f9fbe7';
    this.boxShadows = '#777777';
    this.dropShadows = '#777777';
    this.link = '#827717';
    this.hoverFocus = '#558b2f';
    this.buttonColor = '#212121';
    this.buttonBackground = '#c4e08a';
    this.buttonBorder = '#c4e08a';
    this.fontFamilySansSerif = '';
    this.fontFamilyMonospace = '';
    this.h1Size = '21px';
    this.h1Color = '#212121';
    this.h2Size = '#18px';
    this.h2Color = '#212121';
    this.h3Size = '#15px';
    this.h3Color = '#212121';
    // Primary Colors
    document.documentElement.style.setProperty('--shade-base', this.shadeBase);
    document.documentElement.style.setProperty('--shade-one', this.shadeOne);
    document.documentElement.style.setProperty('--shade-two', this.shadeTwo);
    document.documentElement.style.setProperty('--shade-three', this.shadeThree);
    document.documentElement.style.setProperty('--shade-four', this.shadeFour);
    document.documentElement.style.setProperty('--neutral', this.neutral);
    document.documentElement.style.setProperty('--offset-one', this.offsetOne);
    document.documentElement.style.setProperty('--offset-two', this.offsetTwo);
    document.documentElement.style.setProperty('--offset-three', this.offsetThree);
    document.documentElement.style.setProperty('--offset-four', this.offsetFour);
    // Accent Colors
    document.documentElement.style.setProperty('--accent-one', this.accentOne);
    document.documentElement.style.setProperty('--accent-two', this.accentTwo);
    document.documentElement.style.setProperty('--accent-three', this.accentThree);
    document.documentElement.style.setProperty('--accent-four', this.offsetFour);
    // Angular
    document.documentElement.style.setProperty('--text-icons', this.shadeBase); // shade base
    document.documentElement.style.setProperty('--primary-text', this.shadeOne); // shade one
    document.documentElement.style.setProperty('--dark', this.shadeTwo); // shade two
    document.documentElement.style.setProperty('--dark-primary-color', this.shadeThree); // shade three
    document.documentElement.style.setProperty('--secondary-text', this.offsetOne); // offset one
    document.documentElement.style.setProperty('--divider-color', this.offsetTwo); // offset two
    document.documentElement.style.setProperty('--light-primary-color', this.offsetThree); // offset three
    document.documentElement.style.setProperty('--light', this.offsetFour); // offset four
    document.documentElement.style.setProperty('--warning', this.accentOne); // accent one
    document.documentElement.style.setProperty('--secondary', this.accentTwo); // accent two
    document.documentElement.style.setProperty('--info', this.accentFour); // accent four
    //Bootstrap
    document.documentElement.style.setProperty('--bs-primary', this.neutral); // neutral
    document.documentElement.style.setProperty('--bs-secondary', this.offsetOne); // accent two
    document.documentElement.style.setProperty('--bs-info', this.offsetOne); // offset three
    document.documentElement.style.setProperty('--bs-warning', this.offsetOne); // DEEP ORANGE
    document.documentElement.style.setProperty('--bs-danger', this.offsetOne); // DEEP RED
    document.documentElement.style.setProperty('--bs-light', this.shadeTwo); // --shade-two
    document.documentElement.style.setProperty('--bs-dark', this.offsetTwo); // --offset-two
    // Custom Defined
    document.documentElement.style.setProperty('--header-background', this.headerBackground);
    document.documentElement.style.setProperty('--header-color', this.headerColor);
    document.documentElement.style.setProperty('--footer-background', this.footerBackground);
    document.documentElement.style.setProperty('--footer-color', this.footerColor);
    document.documentElement.style.setProperty('--body-background', this.bodyBackground);
    document.documentElement.style.setProperty('--body-color', this.bodyColor);
    document.documentElement.style.setProperty('--button-background', this.buttonBackground);
    document.documentElement.style.setProperty('--button-border', this.buttonBorder);
    document.documentElement.style.setProperty('--button-color', this.buttonColor);
    document.documentElement.style.setProperty('--box-shadows', this.boxShadows);
    document.documentElement.style.setProperty('--drop-shadows', this.dropShadows);
    document.documentElement.style.setProperty('--link', this.link);
    document.documentElement.style.setProperty('--hover-focus', this.hoverFocus);
    document.body.className = '';
    $('body').addClass(this._typeOf);
  }

  setYellow() {
    this.removeColors();
    this._starterTheme = 'yellow';
    this._typeOf = 'mat-yellow';
    this.shadeBase = '#fffde7';
    this.shadeOne = '#fff9c4';
    this.shadeTwo = '#fff59d';
    this.shadeThree = '#fff176';
    this.shadeFour = '#ffee58';
    this.neutral = '#ffeb3b';
    this.offsetOne = '#fdd835';
    this.offsetTwo = '#fbc02d';
    this.offsetThree = '#f9a825';
    this.offsetFour = '#f57f17';
    this.accentOne = '#ffff8d';
    this.accentTwo = '#ffff00';
    this.accentThree = '#ffea00';
    this.accentFour = '#ffd600';
    // set Local Variables
    this.headerBackground = '#bdc8a1';
    this.headerColor = '#212121';
    this.footerBackground = '#bdc8a1';
    this.footerColor = '#212121';
    this.bodyColor = '#212121';
    this.bodyBackground = '#f9fbe7';
    this.boxShadows = '#777777';
    this.dropShadows = '#777777';
    this.link = '#827717';
    this.hoverFocus = '#558b2f';
    this.buttonColor = '#212121';
    this.buttonBackground = '#c4e08a';
    this.buttonBorder = '#c4e08a';
    this.fontFamilySansSerif = '';
    this.fontFamilyMonospace = '';
    this.h1Size = '21px';
    this.h1Color = '#212121';
    this.h2Size = '#18px';
    this.h2Color = '#212121';
    this.h3Size = '#15px';
    this.h3Color = '#212121';
    // Primary Colors
    document.documentElement.style.setProperty('--shade-base', this.shadeBase);
    document.documentElement.style.setProperty('--shade-one', this.shadeOne);
    document.documentElement.style.setProperty('--shade-two', this.shadeTwo);
    document.documentElement.style.setProperty('--shade-three', this.shadeThree);
    document.documentElement.style.setProperty('--shade-four', this.shadeFour);
    document.documentElement.style.setProperty('--neutral', this.neutral);
    document.documentElement.style.setProperty('--offset-one', this.offsetOne);
    document.documentElement.style.setProperty('--offset-two', this.offsetTwo);
    document.documentElement.style.setProperty('--offset-three', this.offsetThree);
    document.documentElement.style.setProperty('--offset-four', this.offsetFour);
    // Accent Colors
    document.documentElement.style.setProperty('--accent-one', this.accentOne);
    document.documentElement.style.setProperty('--accent-two', this.accentTwo);
    document.documentElement.style.setProperty('--accent-three', this.accentThree);
    document.documentElement.style.setProperty('--accent-four', this.offsetFour);
    // Angular
    document.documentElement.style.setProperty('--text-icons', this.shadeBase); // shade base
    document.documentElement.style.setProperty('--primary-text', this.shadeOne); // shade one
    document.documentElement.style.setProperty('--dark', this.shadeTwo); // shade two
    document.documentElement.style.setProperty('--dark-primary-color', this.shadeThree); // shade three
    document.documentElement.style.setProperty('--secondary-text', this.offsetOne); // offset one
    document.documentElement.style.setProperty('--divider-color', this.offsetTwo); // offset two
    document.documentElement.style.setProperty('--light-primary-color', this.offsetThree); // offset three
    document.documentElement.style.setProperty('--light', this.offsetFour); // offset four
    document.documentElement.style.setProperty('--warning', this.accentOne); // accent one
    document.documentElement.style.setProperty('--secondary', this.accentTwo); // accent two
    document.documentElement.style.setProperty('--info', this.accentFour); // accent four
    //Bootstrap
    document.documentElement.style.setProperty('--bs-primary', this.neutral); // neutral
    document.documentElement.style.setProperty('--bs-secondary', this.offsetOne); // accent two
    document.documentElement.style.setProperty('--info', this.offsetOne); // offset three
    document.documentElement.style.setProperty('--warning', this.offsetOne); // DEEP ORANGE
    document.documentElement.style.setProperty('--danger', this.offsetOne); // DEEP RED
    document.documentElement.style.setProperty('--light', this.shadeTwo); // --shade-two
    document.documentElement.style.setProperty('--dark', this.offsetTwo); // --offset-two
    // Custom Defined
    document.documentElement.style.setProperty('--header-background', this.headerBackground);
    document.documentElement.style.setProperty('--header-color', this.headerColor);
    document.documentElement.style.setProperty('--footer-background', this.footerBackground);
    document.documentElement.style.setProperty('--footer-color', this.footerColor);
    document.documentElement.style.setProperty('--body-background', this.bodyBackground);
    document.documentElement.style.setProperty('--body-color', this.bodyColor);
    document.documentElement.style.setProperty('--button-background', this.buttonBackground);
    document.documentElement.style.setProperty('--button-border', this.buttonBorder);
    document.documentElement.style.setProperty('--button-color', this.buttonColor);
    document.documentElement.style.setProperty('--box-shadows', this.boxShadows);
    document.documentElement.style.setProperty('--drop-shadows', this.dropShadows);
    document.documentElement.style.setProperty('--link', this.link);
    document.documentElement.style.setProperty('--hover-focus', this.hoverFocus);
    document.body.className = '';
    $('body').addClass(this._typeOf);
  }

  setAmber() {
    this.removeColors();
    this._starterTheme = 'amber';
    this._typeOf = 'mat-amber';
    this.shadeBase = '#fff8e1';
    this.shadeOne = '#ffecb3';
    this.shadeTwo = '#ffe082';
    this.shadeThree = '#ffd54f';
    this.shadeFour = '#ffca28';
    this.neutral = '#ffc107';
    this.offsetOne = '#ffb300';
    this.offsetTwo = '#ffa000';
    this.offsetThree = '#ff8f00';
    this.offsetFour = '#ff6f00';
    this.accentOne = '#ffe57f';
    this.accentTwo = '#ffd740';
    this.accentThree = '#ffc400';
    this.accentFour = '#ffab00';
    // set Local Variables
    this.headerBackground = '#bdc8a1';
    this.headerColor = '#212121';
    this.footerBackground = '#bdc8a1';
    this.footerColor = '#212121';
    this.bodyColor = '#212121';
    this.bodyBackground = '#f9fbe7';
    this.boxShadows = '#777777';
    this.dropShadows = '#777777';
    this.link = '#827717';
    this.hoverFocus = '#558b2f';
    this.buttonColor = '#212121';
    this.buttonBackground = '#c4e08a';
    this.buttonBorder = '#c4e08a';
    this.fontFamilySansSerif = '';
    this.fontFamilyMonospace = '';
    this.h1Size = '21px';
    this.h1Color = '#212121';
    this.h2Size = '#18px';
    this.h2Color = '#212121';
    this.h3Size = '#15px';
    this.h3Color = '#212121';
    // Primary Colors
    document.documentElement.style.setProperty('--shade-base', this.shadeBase);
    document.documentElement.style.setProperty('--shade-one', this.shadeOne);
    document.documentElement.style.setProperty('--shade-two', this.shadeTwo);
    document.documentElement.style.setProperty('--shade-three', this.shadeThree);
    document.documentElement.style.setProperty('--shade-four', this.shadeFour);
    document.documentElement.style.setProperty('--neutral', this.neutral);
    document.documentElement.style.setProperty('--offset-one', this.offsetOne);
    document.documentElement.style.setProperty('--offset-two', this.offsetTwo);
    document.documentElement.style.setProperty('--offset-three', this.offsetThree);
    document.documentElement.style.setProperty('--offset-four', this.offsetFour);
    // Accent Colors
    document.documentElement.style.setProperty('--accent-one', this.accentOne);
    document.documentElement.style.setProperty('--accent-two', this.accentTwo);
    document.documentElement.style.setProperty('--accent-three', this.accentThree);
    document.documentElement.style.setProperty('--accent-four', this.offsetFour);
    // Angular
    document.documentElement.style.setProperty('--text-icons', this.shadeBase); // shade base
    document.documentElement.style.setProperty('--primary-text', this.shadeOne); // shade one
    document.documentElement.style.setProperty('--dark', this.shadeTwo); // shade two
    document.documentElement.style.setProperty('--dark-primary-color', this.shadeThree); // shade three
    document.documentElement.style.setProperty('--secondary-text', this.offsetOne); // offset one
    document.documentElement.style.setProperty('--divider-color', this.offsetTwo); // offset two
    document.documentElement.style.setProperty('--light-primary-color', this.offsetThree); // offset three
    document.documentElement.style.setProperty('--light', this.offsetFour); // offset four
    document.documentElement.style.setProperty('--warning', this.accentOne); // accent one
    document.documentElement.style.setProperty('--secondary', this.accentTwo); // accent two
    document.documentElement.style.setProperty('--info', this.accentFour); // accent four
    //Bootstrap
    document.documentElement.style.setProperty('--bs-primary', this.neutral); // neutral
    document.documentElement.style.setProperty('--bs-secondary', this.offsetOne); // accent two
    document.documentElement.style.setProperty('--bs-info', this.offsetOne); // offset three
    document.documentElement.style.setProperty('--bs-warning', this.offsetOne); // DEEP ORANGE
    document.documentElement.style.setProperty('--bs-danger', this.offsetOne); // DEEP RED
    document.documentElement.style.setProperty('--bs-light', this.shadeTwo); // --shade-two
    document.documentElement.style.setProperty('--bs-dark', this.offsetTwo); // --offset-two
    // Custom Defined
    document.documentElement.style.setProperty('--header-background', this.headerBackground);
    document.documentElement.style.setProperty('--header-color', this.headerColor);
    document.documentElement.style.setProperty('--footer-background', this.footerBackground);
    document.documentElement.style.setProperty('--footer-color', this.footerColor);
    document.documentElement.style.setProperty('--body-background', this.bodyBackground);
    document.documentElement.style.setProperty('--body-color', this.bodyColor);
    document.documentElement.style.setProperty('--button-background', this.buttonBackground);
    document.documentElement.style.setProperty('--button-border', this.buttonBorder);
    document.documentElement.style.setProperty('--button-color', this.buttonColor);
    document.documentElement.style.setProperty('--box-shadows', this.boxShadows);
    document.documentElement.style.setProperty('--drop-shadows', this.dropShadows);
    document.documentElement.style.setProperty('--link', this.link);
    document.documentElement.style.setProperty('--hover-focus', this.hoverFocus);
    document.body.className = '';
    $('body').addClass(this._typeOf);
  }

  setOrange() {
    this.removeColors();
    this.themeName = 'Orange';
    this._starterTheme = 'orange';
    this._typeOf = 'mat-orange';
    this.shadeBase = '#fff7f3';
    this.shadeOne = '#ffe0b2';
    this.shadeTwo = '#ffcc80';
    this.shadeThree = '#ffb74d';
    // this.shadeFour ='#ffa726';
    this.shadeFour = '#000000';
    this.neutral = '#ff9800';
    this.offsetOne = '#fb8c00';
    this.offsetTwo = '#ffa242';
    this.offsetThree = '#ffb77c';
    this.offsetFour = '#e65100';
    this.accentOne = '#ffd180';
    this.accentTwo = '#ffab40';
    this.accentThree = '#804700';
    this.accentFour = '#ff6d00';
    // set Local Variables
    this.headerBackground = '#ffe0b2';
    this.headerColor = '#000000';
    this.footerBackground = '#ffe0b2';
    this.footerColor = '#000000';
    this.boxShadows = '#000000';
    this.dropShadows = '#000000';
    this.link = '#e65100';
    this.hoverFocus = '#ef6c00';
    this.bodyColor = '#000000';
    this.bodyBackground = '#FEF9F6';
    this.buttonColor = '#ffb74d';
    this.buttonBackground = '#ff9800';
    this.buttonBorder = '#ff9800';

    this.fontFamilySansSerif = ' ';
    this.fontFamilyMonospace = ' ';
    this.h1Size = '21px';
    this.h1Color = '#212121';
    this.h2Size = '#18px';
    this.h2Color = '#212121';
    this.h3Size = '#15px';
    this.h3Color = '#212121';

    // ORANGE----------------------------
    // #DC6718
    // #FAE1D1
    // #FEF9F6
    // #FDF2EC
    // #F4C19F

    // Primary Colors
    document.documentElement.style.setProperty('--shade-base', this.shadeBase);
    document.documentElement.style.setProperty('--shade-one', this.shadeOne);
    document.documentElement.style.setProperty('--shade-two', this.shadeTwo);
    document.documentElement.style.setProperty('--shade-three', this.shadeThree);
    document.documentElement.style.setProperty('--shade-four', this.shadeFour);
    document.documentElement.style.setProperty('--neutral', this.neutral);
    document.documentElement.style.setProperty('--offset-one', this.offsetOne);
    document.documentElement.style.setProperty('--offset-two', this.offsetTwo);
    document.documentElement.style.setProperty('--offset-three', this.offsetThree);
    document.documentElement.style.setProperty('--offset-four', this.offsetFour);
    // Accent Colors
    document.documentElement.style.setProperty('--accent-one', this.accentOne);
    document.documentElement.style.setProperty('--accent-two', this.accentTwo);
    document.documentElement.style.setProperty('--accent-three', this.accentThree);
    document.documentElement.style.setProperty('--accent-four', this.offsetFour);
    // Angular
    document.documentElement.style.setProperty('--text-icons', this.shadeBase); // shade base
    document.documentElement.style.setProperty('--primary-text', this.shadeOne); // shade one
    document.documentElement.style.setProperty('--dark', this.shadeTwo); // shade two
    document.documentElement.style.setProperty('--dark-primary-color', this.shadeThree); // shade three
    document.documentElement.style.setProperty('--secondary-text', this.offsetOne); // offset one
    document.documentElement.style.setProperty('--divider-color', this.offsetTwo); // offset two
    document.documentElement.style.setProperty('--light-primary-color', this.offsetThree); // offset three
    document.documentElement.style.setProperty('--light', this.offsetFour); // offset four
    document.documentElement.style.setProperty('--warning', this.accentOne); // accent one
    document.documentElement.style.setProperty('--bs-secondary', this.accentTwo); // accent two
    document.documentElement.style.setProperty('--info', this.accentFour); // accent four
    //Bootstrap
    document.documentElement.style.setProperty('--bs-primary', this.neutral); // neutral
    document.documentElement.style.setProperty('--bs-secondary', this.offsetOne); // accent two
    document.documentElement.style.setProperty('--bs-info', this.offsetOne); // offset three
    document.documentElement.style.setProperty('--bs-warning', this.offsetOne); // DEEP ORANGE
    document.documentElement.style.setProperty('--bs-danger', this.offsetOne); // DEEP RED
    document.documentElement.style.setProperty('--bs-light', this.shadeTwo); // --shade-two
    document.documentElement.style.setProperty('--bs-dark', this.offsetTwo); // --offset-two
    // Custom Defined
    document.documentElement.style.setProperty('--header-background', this.headerBackground);
    document.documentElement.style.setProperty('--header-color', this.headerColor);
    document.documentElement.style.setProperty('--footer-background', this.footerBackground);
    document.documentElement.style.setProperty('--footer-color', this.footerColor);
    document.documentElement.style.setProperty('--body-background', this.bodyBackground);
    document.documentElement.style.setProperty('--body-color', this.bodyColor);
    document.documentElement.style.setProperty('--button-background', this.buttonBackground);
    document.documentElement.style.setProperty('--button-border', this.buttonBorder);
    document.documentElement.style.setProperty('--button-color', this.buttonColor);
    document.documentElement.style.setProperty('--box-shadows', this.boxShadows);
    document.documentElement.style.setProperty('--drop-shadows', this.dropShadows);
    document.documentElement.style.setProperty('--link', this.link);
    document.documentElement.style.setProperty('--hover-focus', this.hoverFocus);
    document.body.className = '';
    $('body').addClass(this._typeOf);
  }

  setDeepOrange() {
    this.removeColors();
    this._starterTheme = 'deeporange';
    this._typeOf = 'mat-deep-orange';
    this.shadeBase = '#fbe9e7';
    this.shadeOne = '#ffccbc';
    this.shadeTwo = '#ffab91';
    this.shadeThree = '#ff8a65';
    this.shadeFour = '#ff7043';
    this.neutral = '#ff5722';
    this.offsetOne = '#f4511e';
    this.offsetTwo = '#a53d1c';
    this.offsetThree = '#ff825d';
    this.offsetFour = '#bf360c';
    this.accentOne = '#ff9e80';
    this.accentTwo = '#ff6e40';
    this.accentThree = '#ff3d00';
    this.accentFour = '#dd2c00';
    // set Local Variables
    this.headerBackground = '#ff6200';
    this.headerColor = '#fafafa';
    this.footerBackground = '#ff6200';
    this.footerColor = '#fafafa';
    this.bodyColor = '#212121';
    this.bodyBackground = '#fbe9e7';
    this.boxShadows = '#212121';
    this.dropShadows = '#212121';
    this.link = '#bf360c';
    this.hoverFocus = '#ff8a65';
    this.buttonColor = '#fafafa';
    this.buttonBackground = '#d84315';
    this.buttonBorder = '#d84315';
    this.fontFamilySansSerif = '';
    this.fontFamilyMonospace = '';
    this.h1Size = '21px';
    this.h1Color = '#212121';
    this.h2Size = '#18px';
    this.h2Color = '#212121';
    this.h3Size = '#15px';
    this.h3Color = '#212121';
    // Primary Colors
    document.documentElement.style.setProperty('--shade-base', this.shadeBase);
    document.documentElement.style.setProperty('--shade-one', this.shadeOne);
    document.documentElement.style.setProperty('--shade-two', this.shadeTwo);
    document.documentElement.style.setProperty('--shade-three', this.shadeThree);
    document.documentElement.style.setProperty('--shade-four', this.shadeFour);
    document.documentElement.style.setProperty('--neutral', this.neutral);
    document.documentElement.style.setProperty('--offset-one', this.offsetOne);
    document.documentElement.style.setProperty('--offset-two', this.offsetTwo);
    document.documentElement.style.setProperty('--offset-three', this.offsetThree);
    document.documentElement.style.setProperty('--offset-four', this.offsetFour);
    // Accent Colors
    document.documentElement.style.setProperty('--accent-one', this.accentOne);
    document.documentElement.style.setProperty('--accent-two', this.accentTwo);
    document.documentElement.style.setProperty('--accent-three', this.accentThree);
    document.documentElement.style.setProperty('--accent-four', this.offsetFour);
    // Angular
    document.documentElement.style.setProperty('--text-icons', this.shadeBase); // shade base
    document.documentElement.style.setProperty('--primary-text', this.shadeOne); // shade one
    document.documentElement.style.setProperty('--dark', this.shadeTwo); // shade two
    document.documentElement.style.setProperty('--dark-primary-color', this.shadeThree); // shade three
    document.documentElement.style.setProperty('--secondary-text', this.offsetOne); // offset one
    document.documentElement.style.setProperty('--divider-color', this.offsetTwo); // offset two
    document.documentElement.style.setProperty('--light-primary-color', this.offsetThree); // offset three
    document.documentElement.style.setProperty('--light', this.offsetFour); // offset four
    document.documentElement.style.setProperty('--warning', this.accentOne); // accent one
    document.documentElement.style.setProperty('--secondary', this.accentTwo); // accent two
    document.documentElement.style.setProperty('--info', this.accentFour); // accent four
    //Bootstrap
    document.documentElement.style.setProperty('--bs-primary', this.neutral); // neutral
    document.documentElement.style.setProperty('--bs-secondary', this.offsetOne); // accent two
    document.documentElement.style.setProperty('--bs-info', this.offsetOne); // offset three
    document.documentElement.style.setProperty('--bs-warning', this.offsetOne); // DEEP ORANGE
    document.documentElement.style.setProperty('--bs-danger', this.offsetOne); // DEEP RED
    document.documentElement.style.setProperty('--bs-light', this.shadeTwo); // --shade-two
    document.documentElement.style.setProperty('--bs-dark', this.offsetTwo); // --offset-two
    // Custom Defined
    document.documentElement.style.setProperty('--header-background', this.headerBackground);
    document.documentElement.style.setProperty('--header-color', this.headerColor);
    document.documentElement.style.setProperty('--footer-background', this.footerBackground);
    document.documentElement.style.setProperty('--footer-color', this.footerColor);
    document.documentElement.style.setProperty('--body-background', this.bodyBackground);
    document.documentElement.style.setProperty('--body-color', this.bodyColor);
    document.documentElement.style.setProperty('--button-background', this.buttonBackground);
    document.documentElement.style.setProperty('--button-border', this.buttonBorder);
    document.documentElement.style.setProperty('--button-color', this.buttonColor);
    document.documentElement.style.setProperty('--box-shadows', this.boxShadows);
    document.documentElement.style.setProperty('--drop-shadows', this.dropShadows);
    document.documentElement.style.setProperty('--link', this.link);
    document.documentElement.style.setProperty('--hover-focus', this.hoverFocus);
    document.body.className = '';
    $('body').addClass(this._typeOf);
  }

  setRed() {
    this.removeColors();
    this.themeName = 'Red';
    this._starterTheme = 'red';
    this._typeOf = 'mat-red';
    this.shadeBase = '#ffebee';
    this.shadeOne = '#ffcdd2';
    this.shadeTwo = '#ef9a9a';
    this.shadeThree = '#e57373';
    // this.shadeFour ='#F16D6A';
    this.shadeFour = '#000000';
    this.neutral = '#B88E95';
    this.offsetOne = '#C58C93';
    this.offsetTwo = '#C99CA2';
    this.offsetThree = '#C6858B';
    this.offsetFour = '#E8B0BD';
    this.accentOne = '#ff8a80';
    this.accentTwo = '#FF6161';
    this.accentThree = '#AD0005';
    this.accentFour = '#FF6161';
    // set Local Variables
    this.headerBackground = '#FF6B6B';
    this.headerColor = '#000000';
    this.footerBackground = '#FF6B6B';
    this.footerColor = '#000000';
    this.boxShadows = '#000000';
    this.dropShadows = '#000000';
    this.link = '#FF5C79';
    this.hoverFocus = '#FF5C79';
    this.bodyColor = '#000000';
    this.bodyBackground = '#FFFFFF';
    this.buttonColor = '#fafafa';
    this.buttonBackground = '#FF99AE';
    this.buttonBorder = '#FF99AE';

    this.fontFamilySansSerif = '';
    this.fontFamilyMonospace = '';
    this.h1Size = '21px';
    this.h1Color = '#212121';
    this.h2Size = '#18px';
    this.h2Color = '#212121';
    this.h3Size = '#15px';
    this.h3Color = '#212121';
    // Primary Colors
    document.documentElement.style.setProperty('--shade-base', this.shadeBase);
    document.documentElement.style.setProperty('--shade-one', this.shadeOne);
    document.documentElement.style.setProperty('--shade-two', this.shadeTwo);
    document.documentElement.style.setProperty('--shade-three', this.shadeThree);
    document.documentElement.style.setProperty('--shade-four', this.shadeFour);
    document.documentElement.style.setProperty('--neutral', this.neutral);
    document.documentElement.style.setProperty('--offset-one', this.offsetOne);
    document.documentElement.style.setProperty('--offset-two', this.offsetTwo);
    document.documentElement.style.setProperty('--offset-three', this.offsetThree);
    document.documentElement.style.setProperty('--offset-four', this.offsetFour);
    // Accent Colors
    document.documentElement.style.setProperty('--accent-one', this.accentOne);
    document.documentElement.style.setProperty('--accent-two', this.accentTwo);
    document.documentElement.style.setProperty('--accent-three', this.accentThree);
    document.documentElement.style.setProperty('--accent-four', this.offsetFour);
    // Angular
    document.documentElement.style.setProperty('--text-icons', this.shadeBase); // shade base
    document.documentElement.style.setProperty('--primary-text', this.shadeOne); // shade one
    document.documentElement.style.setProperty('--dark', this.shadeTwo); // shade two
    document.documentElement.style.setProperty('--dark-primary-color', this.shadeThree); // shade three
    document.documentElement.style.setProperty('--secondary-text', this.offsetOne); // offset one
    document.documentElement.style.setProperty('--divider-color', this.offsetTwo); // offset two
    document.documentElement.style.setProperty('--light-primary-color', this.offsetThree); // offset three
    document.documentElement.style.setProperty('--light', this.offsetFour); // offset four
    document.documentElement.style.setProperty('--warning', this.accentOne); // accent one
    document.documentElement.style.setProperty('--secondary', this.accentTwo); // accent two
    document.documentElement.style.setProperty('--info', this.accentFour); // accent four
    //Bootstrap
    document.documentElement.style.setProperty('--bs-primary', this.neutral); // neutral
    document.documentElement.style.setProperty('--bs-secondary', this.offsetOne); // accent two
    document.documentElement.style.setProperty('--bs-info', this.offsetOne); // offset three
    document.documentElement.style.setProperty('--bs-warning', this.offsetOne); // DEEP ORANGE
    document.documentElement.style.setProperty('--bs-danger', this.offsetOne); // DEEP RED
    document.documentElement.style.setProperty('--bs-light', this.shadeTwo); // --shade-two
    document.documentElement.style.setProperty('--bs-dark', this.offsetTwo); // --offset-two
    // Custom Defined
    document.documentElement.style.setProperty('--header-background', this.headerBackground);
    document.documentElement.style.setProperty('--header-color', this.headerColor);
    document.documentElement.style.setProperty('--footer-background', this.footerBackground);
    document.documentElement.style.setProperty('--footer-color', this.footerColor);
    document.documentElement.style.setProperty('--body-background', this.bodyBackground);
    document.documentElement.style.setProperty('--body-color', this.bodyColor);
    document.documentElement.style.setProperty('--button-background', this.buttonBackground);
    document.documentElement.style.setProperty('--button-border', this.buttonBorder);
    document.documentElement.style.setProperty('--button-color', this.buttonColor);
    document.documentElement.style.setProperty('--box-shadows', this.boxShadows);
    document.documentElement.style.setProperty('--drop-shadows', this.dropShadows);
    document.documentElement.style.setProperty('--link', this.link);
    document.documentElement.style.setProperty('--hover-focus', this.hoverFocus);
    document.body.className = '';
    $('body').addClass(this._typeOf);
  }

  setPink() {
    this.removeColors();
    this.themeName = 'Pink';
    this._starterTheme = 'pink';
    this._typeOf = 'mat-pink';
    this.shadeBase = '#fce4ec';
    this.shadeOne = '#f8bbd0';
    this.shadeTwo = '#f48fb1';
    this.shadeThree = '#F16A98';
    // this.shadeFour ='#F06B98';
    this.shadeFour = '#000000';
    this.neutral = '#F16F9D';
    this.offsetOne = '#EE6898';
    this.offsetTwo = '#fa87b0';
    this.offsetThree = '#ff95c3';
    this.offsetFour = '#EF62B0';
    this.accentOne = '#ff80ab';
    this.accentTwo = '#FF9EC0';
    this.accentThree = '#9E0024';
    this.accentFour = '#F363A2';
    // set Local Variables
    this.headerBackground = '#ffbdd3';
    this.headerColor = '#000000';
    this.footerBackground = '#ffbdd3';
    this.footerColor = '#000000';
    this.boxShadows = '#000000';
    this.dropShadows = '#000000';
    this.link = '#F165B5';
    this.hoverFocus = '#FF5791';
    this.bodyColor = '#000000';
    this.bodyBackground = '#fce4ec';
    this.buttonColor = '#fafafa';
    this.buttonBackground = '#F58FB5';
    this.buttonBorder = '#F58FB5';

    this.fontFamilySansSerif = '';
    this.fontFamilyMonospace = '';
    this.h1Size = '21px';
    this.h1Color = '#212121';
    this.h2Size = '#18px';
    this.h2Color = '#212121';
    this.h3Size = '#15px';
    this.h3Color = '#212121';
    // Primary Colors
    document.documentElement.style.setProperty('--shade-base', this.shadeBase);
    document.documentElement.style.setProperty('--shade-one', this.shadeOne);
    document.documentElement.style.setProperty('--shade-two', this.shadeTwo);
    document.documentElement.style.setProperty('--shade-three', this.shadeThree);
    document.documentElement.style.setProperty('--shade-four', this.shadeFour);
    document.documentElement.style.setProperty('--neutral', this.neutral);
    document.documentElement.style.setProperty('--offset-one', this.offsetOne);
    document.documentElement.style.setProperty('--offset-two', this.offsetTwo);
    document.documentElement.style.setProperty('--offset-three', this.offsetThree);
    document.documentElement.style.setProperty('--offset-four', this.offsetFour);
    // Accent Colors
    document.documentElement.style.setProperty('--accent-one', this.accentOne);
    document.documentElement.style.setProperty('--accent-two', this.accentTwo);
    document.documentElement.style.setProperty('--accent-three', this.accentThree);
    document.documentElement.style.setProperty('--accent-four', this.offsetFour);
    // Angular
    document.documentElement.style.setProperty('--text-icons', this.shadeBase); // shade base
    document.documentElement.style.setProperty('--primary-text', this.shadeOne); // shade one
    document.documentElement.style.setProperty('--dark', this.shadeTwo); // shade two
    document.documentElement.style.setProperty('--dark-primary-color', this.shadeThree); // shade three
    document.documentElement.style.setProperty('--secondary-text', this.offsetOne); // offset one
    document.documentElement.style.setProperty('--divider-color', this.offsetTwo); // offset two
    document.documentElement.style.setProperty('--light-primary-color', this.offsetThree); // offset three
    document.documentElement.style.setProperty('--light', this.offsetFour); // offset four
    document.documentElement.style.setProperty('--warning', this.accentOne); // accent one
    document.documentElement.style.setProperty('--secondary', this.accentTwo); // accent two
    document.documentElement.style.setProperty('--info', this.accentFour); // accent four
    //Bootstrap
    document.documentElement.style.setProperty('--bs-primary', this.neutral); // neutral
    document.documentElement.style.setProperty('--bs-secondary', this.offsetOne); // accent two
    document.documentElement.style.setProperty('--bs-info', this.offsetOne); // offset three
    document.documentElement.style.setProperty('--bs-warning', this.offsetOne); // DEEP ORANGE
    document.documentElement.style.setProperty('--bs-danger', this.offsetOne); // DEEP RED
    document.documentElement.style.setProperty('--bs-light', this.shadeTwo); // --shade-two
    document.documentElement.style.setProperty('--bs-dark', this.offsetTwo); // --offset-two
    // Custom Defined
    document.documentElement.style.setProperty('--header-background', this.headerBackground);
    document.documentElement.style.setProperty('--header-color', this.headerColor);
    document.documentElement.style.setProperty('--footer-background', this.footerBackground);
    document.documentElement.style.setProperty('--footer-color', this.footerColor);
    document.documentElement.style.setProperty('--body-background', this.bodyBackground);
    document.documentElement.style.setProperty('--body-color', this.bodyColor);
    document.documentElement.style.setProperty('--button-background', this.buttonBackground);
    document.documentElement.style.setProperty('--button-border', this.buttonBorder);
    document.documentElement.style.setProperty('--button-color', this.buttonColor);
    document.documentElement.style.setProperty('--box-shadows', this.boxShadows);
    document.documentElement.style.setProperty('--drop-shadows', this.dropShadows);
    document.documentElement.style.setProperty('--link', this.link);
    document.documentElement.style.setProperty('--hover-focus', this.hoverFocus);
    document.body.className = '';
    $('body').addClass(this._typeOf);
  }

  setBrown() {
    this.removeColors();
    this.themeName = 'Brown';
    this._starterTheme = 'brown';
    this._typeOf = 'mat-brown';
    this.shadeBase = '#efebe9';
    this.shadeOne = '#d7ccc8';
    this.shadeTwo = '#bcaaa4';
    this.shadeThree = '#AA958D';
    // this.shadeFour ='#B4A49C';
    this.shadeFour = '#000000';
    this.neutral = '#B58F82';
    this.offsetOne = '#BB998B';
    this.offsetTwo = '#AA9A92';
    this.offsetThree = '#C3B9B6';
    this.offsetFour = '#BC908A';
    this.accentOne = '#eeeeee';
    this.accentTwo = '#9e9e9e';
    this.accentThree = '#4A4A4A';
    this.accentFour = '#9C9C9C';
    // set Local Variables
    this.headerBackground = '#B48C83';
    this.headerColor = '#000000';
    this.footerBackground = '#B48C83';
    this.footerColor = '#000000';
    this.bodyColor = '#000000';
    this.bodyBackground = '#f1eded';
    this.boxShadows = '#000000';
    this.dropShadows = '#000000';
    this.link = '#BA8C87';
    this.hoverFocus = '#BA8C87';
    this.buttonColor = '#fafafa';
    this.buttonBackground = '#ACA09A';
    this.buttonBorder = '#ACA09A';

    this.fontFamilySansSerif = '';
    this.fontFamilyMonospace = '';
    this.h1Size = '21px';
    this.h1Color = '#212121';
    this.h2Size = '#18px';
    this.h2Color = '#212121';
    this.h3Size = '#15px';
    this.h3Color = '#212121';
    // Primary Colors
    document.documentElement.style.setProperty('--shade-base', this.shadeBase);
    document.documentElement.style.setProperty('--shade-one', this.shadeOne);
    document.documentElement.style.setProperty('--shade-two', this.shadeTwo);
    document.documentElement.style.setProperty('--shade-three', this.shadeThree);
    document.documentElement.style.setProperty('--shade-four', this.shadeFour);
    document.documentElement.style.setProperty('--neutral', this.neutral);
    document.documentElement.style.setProperty('--offset-one', this.offsetOne);
    document.documentElement.style.setProperty('--offset-two', this.offsetTwo);
    document.documentElement.style.setProperty('--offset-three', this.offsetThree);
    document.documentElement.style.setProperty('--offset-four', this.offsetFour);
    // Accent Colors
    document.documentElement.style.setProperty('--accent-one', this.accentOne);
    document.documentElement.style.setProperty('--accent-two', this.accentTwo);
    document.documentElement.style.setProperty('--accent-three', this.accentThree);
    document.documentElement.style.setProperty('--accent-four', this.offsetFour);
    // Angular
    document.documentElement.style.setProperty('--text-icons', this.shadeBase); // shade base
    document.documentElement.style.setProperty('--primary-text', this.shadeOne); // shade one
    document.documentElement.style.setProperty('--dark', this.shadeTwo); // shade two
    document.documentElement.style.setProperty('--dark-primary-color', this.shadeThree); // shade three
    document.documentElement.style.setProperty('--secondary-text', this.offsetOne); // offset one
    document.documentElement.style.setProperty('--divider-color', this.offsetTwo); // offset two
    document.documentElement.style.setProperty('--light-primary-color', this.offsetThree); // offset three
    document.documentElement.style.setProperty('--light', this.offsetFour); // offset four
    document.documentElement.style.setProperty('--warning', this.accentOne); // accent one
    document.documentElement.style.setProperty('--secondary', this.accentTwo); // accent two
    document.documentElement.style.setProperty('--info', this.accentFour); // accent four
    //Bootstrap
    document.documentElement.style.setProperty('--bs-primary', this.neutral); // neutral
    // Custom Defined
    document.documentElement.style.setProperty('--header-background', this.headerBackground);
    document.documentElement.style.setProperty('--header-color', this.headerColor);
    document.documentElement.style.setProperty('--footer-background', this.footerBackground);
    document.documentElement.style.setProperty('--footer-color', this.footerColor);
    document.documentElement.style.setProperty('--body-background', this.bodyBackground);
    document.documentElement.style.setProperty('--body-color', this.bodyColor);
    document.documentElement.style.setProperty('--button-background', this.buttonBackground);
    document.documentElement.style.setProperty('--button-border', this.buttonBorder);
    document.documentElement.style.setProperty('--button-color', this.buttonColor);
    document.documentElement.style.setProperty('--box-shadows', this.boxShadows);
    document.documentElement.style.setProperty('--drop-shadows', this.dropShadows);
    document.documentElement.style.setProperty('--link', this.link);
    document.documentElement.style.setProperty('--hover-focus', this.hoverFocus);
    document.body.className = '';
    $('body').addClass(this._typeOf);
  }

  setBlueGrey() {
    this.removeColors();
    this.themeName = 'Blue Gray';
    this._starterTheme = 'bluegrey';
    this._typeOf = 'mat-blue-grey';
    this.shadeBase = '#eceff1';
    this.shadeOne = '#cfd8dc';
    this.shadeTwo = '#b0bec5';
    this.shadeThree = '#90a4ae';
    // this.shadeFour ='#8498A4';
    this.shadeFour = '#000000';
    this.neutral = '#7D9AA5';
    this.offsetOne = '#7C9CA6';
    this.offsetTwo = '#92A3A5';
    this.offsetThree = '#AEC4C6';
    this.offsetFour = '#A3B8C2';
    this.accentOne = '#eeeeee';
    this.accentTwo = '#9e9e9e';
    this.accentThree = '#4F4F4F';
    this.accentFour = '#A3A3A3';
    // set Local Variables
    this.headerBackground = '#CDDADA';
    this.headerColor = '#000000';
    this.footerBackground = '#CDDADA';
    this.footerColor = '#000000';
    this.bodyColor = '#000000';
    this.bodyBackground = '#eceff1';
    this.boxShadows = '#000000';
    this.dropShadows = '#000000';
    this.link = '#5B96F0';
    this.hoverFocus = '#5B96F0';
    this.buttonColor = '#fafafa';
    this.buttonBackground = '#669EFF';
    this.buttonBorder = '#669EFF';

    this.fontFamilySansSerif = '';
    this.fontFamilyMonospace = '';
    this.h1Size = '21px';
    this.h1Color = '#212121';
    this.h2Size = '#18px';
    this.h2Color = '#212121';
    this.h3Size = '#15px';
    this.h3Color = '#212121';
    // Primary Colors
    document.documentElement.style.setProperty('--shade-base', this.shadeBase);
    document.documentElement.style.setProperty('--shade-one', this.shadeOne);
    document.documentElement.style.setProperty('--shade-two', this.shadeTwo);
    document.documentElement.style.setProperty('--shade-three', this.shadeThree);
    document.documentElement.style.setProperty('--shade-four', this.shadeFour);
    document.documentElement.style.setProperty('--neutral', this.neutral);
    document.documentElement.style.setProperty('--offset-one', this.offsetOne);
    document.documentElement.style.setProperty('--offset-two', this.offsetTwo);
    document.documentElement.style.setProperty('--offset-three', this.offsetThree);
    document.documentElement.style.setProperty('--offset-four', this.offsetFour);
    // Accent Colors
    document.documentElement.style.setProperty('--accent-one', this.accentOne);
    document.documentElement.style.setProperty('--accent-two', this.accentTwo);
    document.documentElement.style.setProperty('--accent-three', this.accentThree);
    document.documentElement.style.setProperty('--accent-four', this.offsetFour);
    // Angular
    document.documentElement.style.setProperty('--text-icons', this.shadeBase); // shade base
    document.documentElement.style.setProperty('--primary-text', this.shadeOne); // shade one
    document.documentElement.style.setProperty('--dark', this.shadeTwo); // shade two
    document.documentElement.style.setProperty('--dark-primary-color', this.shadeThree); // shade three
    document.documentElement.style.setProperty('--secondary-text', this.offsetOne); // offset one
    document.documentElement.style.setProperty('--divider-color', this.offsetTwo); // offset two
    document.documentElement.style.setProperty('--light-primary-color', this.offsetThree); // offset three
    document.documentElement.style.setProperty('--light', this.offsetFour); // offset four
    document.documentElement.style.setProperty('--warning', this.accentOne); // accent one
    document.documentElement.style.setProperty('--secondary', this.accentTwo); // accent two
    document.documentElement.style.setProperty('--info', this.accentFour); // accent four
    //Bootstrap
    document.documentElement.style.setProperty('--bs-primary', this.neutral); // neutral
    document.documentElement.style.setProperty('--bs-secondary', this.offsetOne); // accent two
    document.documentElement.style.setProperty('--bs-info', this.offsetOne); // offset three
    document.documentElement.style.setProperty('--bs-warning', this.offsetOne); // DEEP ORANGE
    document.documentElement.style.setProperty('--bs-danger', this.offsetOne); // DEEP RED
    document.documentElement.style.setProperty('--bs-light', this.shadeTwo); // --shade-two
    document.documentElement.style.setProperty('--bs-dark', this.offsetTwo); // --offset-two
    // Custom Defined
    document.documentElement.style.setProperty('--header-background', this.headerBackground);
    document.documentElement.style.setProperty('--header-color', this.headerColor);
    document.documentElement.style.setProperty('--footer-background', this.footerBackground);
    document.documentElement.style.setProperty('--footer-color', this.footerColor);
    document.documentElement.style.setProperty('--body-background', this.bodyBackground);
    document.documentElement.style.setProperty('--body-color', this.bodyColor);
    document.documentElement.style.setProperty('--button-background', this.buttonBackground);
    document.documentElement.style.setProperty('--button-border', this.buttonBorder);
    document.documentElement.style.setProperty('--button-color', this.buttonColor);
    document.documentElement.style.setProperty('--box-shadows', this.boxShadows);
    document.documentElement.style.setProperty('--drop-shadows', this.dropShadows);
    document.documentElement.style.setProperty('--link', this.link);
    document.documentElement.style.setProperty('--hover-focus', this.hoverFocus);
    document.body.className = '';
    $('body').addClass(this._typeOf);
  }

  setRoyal() {
    this.removeColors();

    this.themeName = 'Royal Blue';
    this._starterTheme = 'royal';
    this._typeOf = 'mat-blue';
    this.shadeBase = '#888FE7';
    this.shadeOne = '#cfd8dc';
    this.shadeTwo = '#b0bec5';
    this.shadeThree = '#90a4ae';
    // this.shadeFour ='#8498A4';
    this.shadeFour = '#000000';
    this.neutral = '#7D9AA5';
    this.offsetOne = '#888FE7';
    this.offsetTwo = '#659DC8';
    this.offsetThree = '#b0bec5';
    this.offsetFour = '#999FEB';
    this.accentOne = '#82b1ff';
    this.accentTwo = '#5C98FF';
    this.accentThree = '#619AFF';
    this.accentFour = '#BDCFFF';
    // set Local Variables
    this.headerBackground = '#8B8EE9';
    this.headerColor = '#000000';
    this.footerBackground = '#8B8EE9';
    this.footerColor = '#000000';
    this.boxShadows = '#000000';
    this.dropShadows = '#000000';
    this.link = '#0a19a5';
    this.hoverFocus = '#0019ff';
    this.bodyColor = '#000000';
    this.bodyBackground = '#e8f0f3';
    this.buttonColor = '#fafafa';
    this.buttonBackground = '#7A8EFA';
    this.buttonBorder = '#7A8EFA';

    this.fontFamilySansSerif = '';
    this.fontFamilyMonospace = '';
    this.h1Size = '21px';
    this.h1Color = '#212121';
    this.h2Size = '#18px';
    this.h2Color = '#212121';
    this.h3Size = '#15px';
    this.h3Color = '#212121';

    // Primary Colors
    document.documentElement.style.setProperty('--shade-base', this.shadeBase);
    document.documentElement.style.setProperty('--shade-one', this.shadeOne);
    document.documentElement.style.setProperty('--shade-two', this.shadeTwo);
    document.documentElement.style.setProperty('--shade-three', this.shadeThree);
    document.documentElement.style.setProperty('--shade-four', this.shadeFour);
    document.documentElement.style.setProperty('--neutral', this.neutral);
    document.documentElement.style.setProperty('--offset-one', this.offsetOne);
    document.documentElement.style.setProperty('--offset-two', this.offsetTwo);
    document.documentElement.style.setProperty('--offset-three', this.offsetThree);
    document.documentElement.style.setProperty('--offset-four', this.offsetFour);
    // Accent Colors
    document.documentElement.style.setProperty('--accent-one', this.accentOne);
    document.documentElement.style.setProperty('--accent-two', this.accentTwo);
    document.documentElement.style.setProperty('--accent-three', this.accentThree);
    document.documentElement.style.setProperty('--accent-four', this.offsetFour);
    // Angular
    document.documentElement.style.setProperty('--text-icons', this.shadeBase); // shade base
    document.documentElement.style.setProperty('--primary-text', this.shadeOne); // shade one
    document.documentElement.style.setProperty('--dark', this.shadeTwo); // shade two
    document.documentElement.style.setProperty('--dark-primary-color', this.shadeThree); // shade three
    document.documentElement.style.setProperty('--secondary-text', this.offsetOne); // offset one
    document.documentElement.style.setProperty('--divider-color', this.offsetTwo); // offset two
    document.documentElement.style.setProperty('--light-primary-color', this.offsetThree); // offset three
    document.documentElement.style.setProperty('--light', this.offsetFour); // offset four
    document.documentElement.style.setProperty('--warning', this.accentOne); // accent one
    document.documentElement.style.setProperty('--secondary', this.accentTwo); // accent two
    document.documentElement.style.setProperty('--info', this.accentFour); // accent four
    //Bootstrap
    document.documentElement.style.setProperty('--bs-primary', this.neutral); // neutral
    // Custom Defined
    document.documentElement.style.setProperty('--header-background', this.headerBackground);
    document.documentElement.style.setProperty('--header-color', this.headerColor);
    document.documentElement.style.setProperty('--footer-background', this.footerBackground);
    document.documentElement.style.setProperty('--footer-color', this.footerColor);
    document.documentElement.style.setProperty('--body-background', this.bodyBackground);
    document.documentElement.style.setProperty('--body-color', this.bodyColor);
    document.documentElement.style.setProperty('--button-background', this.buttonBackground);
    document.documentElement.style.setProperty('--button-border', this.buttonBorder);
    document.documentElement.style.setProperty('--button-color', this.buttonColor);
    document.documentElement.style.setProperty('--box-shadows', this.boxShadows);
    document.documentElement.style.setProperty('--drop-shadows', this.dropShadows);
    document.documentElement.style.setProperty('--link', this.link);
    document.documentElement.style.setProperty('--hover-focus', this.hoverFocus);
    document.body.className = '';
    $('body').addClass(this._typeOf);
  }

  //      ---------------------------------------------------------------------------
  //      -------------------------USER EXPERIENCE-----------------------------------
  //      ---------------------------------------------------------------------------

  // snackbar alert
  openSnackBar(message: string, action: string) {
    this._snack.open(message, action, {
      duration: 5000,
      verticalPosition: 'top',
    });
  }

  ngOnDestroy() {
    if (this._headerSubscription) {
      this._headerSubscription.unsubscribe();
    }
  }
}
